import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../components/AppDialog';
import { usePageSEO } from '../hooks/usePageSEO';
import { api } from '../utils/api';
import { startCheckout } from '../utils/stripe';
import ResumePreview from '../components/ResumePreview';
import { SAMPLE_RESUME, SAMPLE_CL, TEMPLATES, CV_TEMPLATES, CL_TEMPLATES } from '../data/samples';
import {
  Sparkles, FileText, Target, BarChart2, Download, Layers,
  Clock, CheckCircle2, Star, ArrowRight, Zap, Users, Award,
  Globe, ShieldCheck, RefreshCw, ChevronDown, ChevronLeft, ChevronRight,
  Mail, Linkedin, Facebook, Instagram, Check, Play, X, Video, Eye, Mic, Briefcase, TrendingUp,
} from 'lucide-react';

const SOCIAL_LINKS = [
  { Icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/123194112/admin/dashboard/' },
  { Icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61590634798131' },
  { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/marsresume/' },
];

const TEMPLATE_SHOWCASE_COPY = {
  modern:        { name: 'Sarah Chen',    role: 'Product Manager' },
  classic:       { name: 'Sarah Chen',    role: 'Operations Manager' },
  executive:     { name: 'Sarah Chen',    role: 'Business Strategist' },
  minimal:       { name: 'Sarah Chen',    role: 'UX Designer' },
  creative:      { name: 'Sarah Chen',    role: 'Creative Director' },
  tech:          { name: 'Sarah Chen',    role: 'Software Engineer' },
  elegant:       { name: 'Sarah Chen',    role: 'Marketing Lead' },
  corporate:     { name: 'Sarah Chen',    role: 'Business Analyst' },
  blue_corporate:{ name: 'Sarah Chen',    role: 'Financial Analyst' },
  navy_timeline:    { name: 'Sarah Chen', role: 'Sales Representative' },
  harvard_standard: { name: 'Sarah Chen', role: 'Senior Software Engineer' },
  crimson:       { name: 'Sarah Chen',    role: 'Marketing Director' },
  academic:      { name: 'Sarah Chen',    role: 'Research Fellow' },
  europass:      { name: 'Sarah Chen',    role: 'Civil Engineer' },
  academic_modern: { name: 'Sarah Chen', role: 'Associate Professor' },
  editorial:     { name: 'Sarah Chen',    role: 'Content Strategist' },
  sage:          { name: 'Sarah Chen',    role: 'HR Manager' },
  graphite:      { name: 'Sarah Chen',    role: 'Growth Marketer' },
};

export const buildShowcaseResume = (tmpl) => {
  const copy = TEMPLATE_SHOWCASE_COPY[tmpl.id] || {};
  return {
    ...SAMPLE_RESUME,
    type: 'resume',
    template: tmpl.id,
    personalInfo: {
      ...SAMPLE_RESUME.personalInfo,
      fullName: copy.name || SAMPLE_RESUME.personalInfo.fullName,
      jobTitle: copy.role || SAMPLE_RESUME.personalInfo.jobTitle,
      photo: null,
      accentColor: '',
      nameColor: '',
      sectionColor: '',
      bodyColor: '',
      sidebarColor: '',
      linkColor: '',
    },
  };
};

export const buildShowcaseCV = (tmpl) => {
  const copy = TEMPLATE_SHOWCASE_COPY[tmpl.id] || {};
  return {
    ...SAMPLE_RESUME,
    type: 'cv',
    template: tmpl.id,
    personalInfo: {
      ...SAMPLE_RESUME.personalInfo,
      fullName: copy.name || SAMPLE_RESUME.personalInfo.fullName,
      jobTitle: copy.role || SAMPLE_RESUME.personalInfo.jobTitle,
      photo: null,
      accentColor: '',
      nameColor: '',
      sectionColor: '',
      bodyColor: '',
      sidebarColor: '',
      linkColor: '',
    },
    publications: [],
  };
};

export const buildShowcaseCL = (tmpl) => ({
  ...SAMPLE_CL,
  template: tmpl.id,
  personalInfo: { ...SAMPLE_CL.personalInfo, accentColor: '' },
});

// Shared across the homepage showcase and the full /templates gallery
export const ATS_FRIENDLY_TEMPLATE_IDS = new Set(['harvard_standard', 'blue_analyst', 'blue_corporate', 'russian_classic', 'classic', 'tech', 'academic']);

const SHOWCASE_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'professional', label: 'Professional' },
  { id: 'creative', label: 'Creative' },
  { id: 'technology', label: 'Technology' },
  { id: 'business', label: 'Business' },
];

const SHOWCASE_TEMPLATE_IDS_BY_CATEGORY = {
  professional: ['modern', 'harvard_standard', 'minimal', 'corporate', 'blue_corporate', 'sky_pro'],
  creative: ['creative', 'rose_gold', 'editorial', 'graphite', 'crimson', 'charcoal_split'],
  technology: ['tech', 'swiss_grid', 'mono_pro', 'bold_stripe'],
  business: ['executive', 'exec_suite', 'boardroom', 'c_suite', 'strategy_blue'],
};
SHOWCASE_TEMPLATE_IDS_BY_CATEGORY.all = [
  ...SHOWCASE_TEMPLATE_IDS_BY_CATEGORY.professional,
  ...SHOWCASE_TEMPLATE_IDS_BY_CATEGORY.creative,
  ...SHOWCASE_TEMPLATE_IDS_BY_CATEGORY.technology,
  ...SHOWCASE_TEMPLATE_IDS_BY_CATEGORY.business,
];

const TemplateShowcaseSection = ({ onBrowse, onUseTemplate }) => {
  const scrollRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const showcaseTemplates = (SHOWCASE_TEMPLATE_IDS_BY_CATEGORY[activeCategory] || [])
    .map(id => TEMPLATES.find(t => t.id === id))
    .filter(Boolean);

  const selectCategory = (id) => {
    setActiveCategory(id);
    if (scrollRef.current) scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
  };

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  return (
    <section style={{ background: 'linear-gradient(180deg,#F8FAFF 0%,#F1F5F9 100%)', padding: '96px 0 80px' }}>
      <style>{`
        .tpl-showcase-tabs {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; flex-wrap: wrap; margin-top: 28px;
        }
        .tpl-showcase-tab {
          background: white; border: 1.5px solid #E2E8F0; color: #475569;
          font-weight: 700; font-size: 13px; padding: 8px 18px; border-radius: 999px;
          cursor: pointer; font-family: inherit; transition: all 0.2s ease;
        }
        .tpl-showcase-tab:hover { border-color: #CBD5E1; color: #1E293B; }
        .tpl-showcase-tab.active { background: #0F172A; border-color: #0F172A; color: white; }

        .tpl-showcase-nav {
          position: absolute; top: 50%; transform: translateY(-50%); z-index: 10;
          width: 44px; height: 44px; border-radius: 50%; background: white;
          border: 1.5px solid #E2E8F0; box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.18s ease; color: #374151;
        }
        .tpl-showcase-nav:hover { background: #0F172A; border-color: #0F172A; color: white; }

        .tpl-showcase-track-wrap {
          overflow-x: auto; scroll-behavior: smooth;
          scrollbar-width: none; -ms-overflow-style: none;
        }
        .tpl-showcase-track-wrap::-webkit-scrollbar { display: none; }
        .tpl-showcase-track { display: flex; gap: 20px; width: max-content; padding: 6px 64px 16px; }

        .tpl-showcase-card {
          position: relative; width: 260px; flex: 0 0 260px;
          border: 1.5px solid #E2E8F0; border-radius: 18px; background: white; overflow: hidden;
          box-shadow: 0 2px 16px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04);
          transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s ease, border-color 0.28s ease;
        }
        .tpl-showcase-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 48px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.06);
          border-color: #CBD5E1;
        }
        .tpl-showcase-thumb { position: relative; height: 300px; overflow: hidden; background: white; }
        .tpl-showcase-ats-badge {
          position: absolute; top: 10px; left: 10px; z-index: 4;
          background: linear-gradient(135deg,#1D4ED8,#2563EB); color: white; border-radius: 6px;
          padding: 3px 9px; font-size: 10px; font-weight: 800; letter-spacing: 0.4px;
          box-shadow: 0 2px 8px rgba(29,78,216,0.35);
        }
        .tpl-showcase-fade {
          position: absolute; bottom: 0; left: 0; right: 0; height: 60px;
          background: linear-gradient(to bottom, transparent, white); pointer-events: none;
        }
        .tpl-showcase-body { padding: 14px 16px 16px; border-top: 1px solid #F1F5F9; }
        .tpl-showcase-name { display: flex; align-items: center; font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 12px; }
        .tpl-showcase-dot { width: 7px; height: 7px; border-radius: 50%; margin-right: 7px; flex-shrink: 0; }
        .tpl-showcase-actions { display: flex; gap: 8px; }
        .tpl-showcase-btn-preview {
          flex: 1; background: white; border: 1.5px solid #E2E8F0; color: #374151;
          font-weight: 700; font-size: 12.5px; padding: 8px 0; border-radius: 9px;
          cursor: pointer; font-family: inherit; transition: all 0.18s ease;
        }
        .tpl-showcase-btn-preview:hover { border-color: #CBD5E1; background: #F9FAFB; }
        .tpl-showcase-btn-use {
          flex: 1; border: none; color: white; font-weight: 700; font-size: 12.5px;
          padding: 8px 0; border-radius: 9px; cursor: pointer; font-family: inherit;
          transition: filter 0.18s ease;
        }
        .tpl-showcase-btn-use:hover { filter: brightness(0.9); }

        @media (max-width: 900px) {
          .tpl-showcase-track { padding: 6px 24px 16px; }
          .tpl-showcase-nav { display: none; }
        }
      `}</style>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 48px', textAlign: 'center' }}>
        <h2 style={{ margin: '0 0 16px', color: '#0F172A', fontFamily: 'Inter, sans-serif', fontSize: 40, lineHeight: 1.15, fontWeight: 900, letterSpacing: '-1px' }}>
          Pick a template. Start building.
        </h2>
        <p style={{ margin: '0 auto', maxWidth: 560, color: '#64748B', fontSize: 16, lineHeight: 1.7 }}>
          Every one is tested against real hiring software. Choose a look and your details flow straight in. No blank pages, no formatting fights.
        </p>

        {/* Category tabs */}
        <div className="tpl-showcase-tabs">
          {SHOWCASE_CATEGORIES.map(cat => (
            <button key={cat.id} type="button" onClick={() => selectCategory(cat.id)}
              className={`tpl-showcase-tab${activeCategory === cat.id ? ' active' : ''}`}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Scroller ── */}
      <div style={{ position: 'relative', marginTop: 40 }}>
        <button className="tpl-showcase-nav" onClick={() => scroll(-1)} style={{ left: 16 }} aria-label="Scroll left">
          <ChevronLeft size={20} />
        </button>

        <div className="tpl-showcase-track-wrap" ref={scrollRef} aria-label="Resume templates">
          <div className="tpl-showcase-track">
            {showcaseTemplates.map((tmpl, index) => {
              const preview = buildShowcaseResume(tmpl);
              const isAts = ATS_FRIENDLY_TEMPLATE_IDS.has(tmpl.id);
              return (
                <div key={`${activeCategory}-${tmpl.id}-${index}`} className="tpl-showcase-card">
                  <div className="tpl-showcase-thumb">
                    {isAts && <span className="tpl-showcase-ats-badge">✓ ATS Friendly</span>}
                    <div style={{ width: 720, transform: 'scale(0.361)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                      <ResumePreview data={preview} id={`showcase-${activeCategory}-${tmpl.id}-${index}`} />
                    </div>
                    <div className="tpl-showcase-fade" />
                  </div>
                  <div className="tpl-showcase-body">
                    <div className="tpl-showcase-name">
                      <span className="tpl-showcase-dot" style={{ background: tmpl.color }} />
                      {tmpl.name}
                    </div>
                    <div className="tpl-showcase-actions">
                      <button type="button" className="tpl-showcase-btn-preview" onClick={onBrowse}>Preview</button>
                      <button type="button" className="tpl-showcase-btn-use" style={{ background: tmpl.color }} onClick={() => onUseTemplate(tmpl.id)}>Use Template</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button className="tpl-showcase-nav" onClick={() => scroll(1)} style={{ right: 16 }} aria-label="Scroll right">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* ── CTA ── */}
      <div style={{ textAlign: 'center', marginTop: 52 }}>
        <button onClick={onBrowse}
          style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', color: 'white', border: 'none', borderRadius: 12, padding: '14px 32px', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 9, transition: 'all 0.2s', boxShadow: '0 4px 16px rgba(15,23,42,0.25)', letterSpacing: -0.2 }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(15,23,42,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(15,23,42,0.25)'; }}>
          Browse all templates <ArrowRight size={16} />
        </button>
        <div style={{ marginTop: 14, fontSize: 12, color: '#94A3B8' }}>No sign-up required to preview</div>
      </div>
    </section>
  );
};

// ── Landing Page ──────────────────────────────────────────────────────────────
const AnimatedCounter = ({ value, decimals = 0, duration = 1800, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

const AnimatedScoreRing = ({ score, size = 240, stroke = 16, color = '#059669', trackColor = '#D1FAE5', duration = 1800, label = 'ATS SCORE' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(score * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(score);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [score, duration]);

  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - display / 100);

  return (
    <div ref={ref} style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.1s linear' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.22, fontWeight: 900, color: '#0D1117', letterSpacing: '-2px', lineHeight: 1 }}>{Math.round(display)}%</div>
        <div style={{ fontSize: 12, fontWeight: 800, color, letterSpacing: 0.6, marginTop: 6 }}>{label}</div>
      </div>
    </div>
  );
};

const InViewFade = ({ children, className = '', style }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} className={`${className}${inView ? ' in-view' : ''}`} style={style}>{children}</div>;
};

const TypewriterWord = ({ words, typingSpeed = 70, deletingSpeed = 40, pauseTime = 1600, style }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    const current = words[index];
    let timeout;

    if (phase === 'typing') {
      if (text.length < current.length) {
        timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), typingSpeed);
      } else {
        timeout = setTimeout(() => setPhase('deleting'), pauseTime);
      }
    } else {
      if (text.length > 0) {
        timeout = setTimeout(() => setText(current.slice(0, text.length - 1)), deletingSpeed);
      } else {
        setPhase('typing');
        setIndex(i => (i + 1) % words.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [text, phase, index, words, typingSpeed, deletingSpeed, pauseTime]);

  return (
    <span style={{ position: 'relative', display: 'inline-block', ...style }}>
      {text}
      <span style={{ display: 'inline-block', width: 5, height: '0.8em', background: 'currentColor', marginLeft: 4, verticalAlign: '-0.05em', animation: 'blinkCursor 1s steps(1) infinite' }} />
    </span>
  );
};

const StarRating = ({ n = 5, color = '#FBBF24', emptyColor = '#D1D5DB', total = 5 }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {Array.from({ length: total }).map((_, i) => (
      <Star key={i} size={14} fill={i < n ? color : 'none'} color={i < n ? color : emptyColor} strokeWidth={1.5} />
    ))}
  </div>
);

export const LandingStyles = () => (
  <style>{`
    @keyframes heroFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes authPulse { 0%,100%{opacity:1} 50%{opacity:0.55} }
    @keyframes slideRight { from{transform:translateX(-8px);opacity:0} to{transform:translateX(0);opacity:1} }
    @keyframes gradShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
    @keyframes spin { to{transform:rotate(360deg)} }
    @keyframes floatIcon { 0%,100%{transform:translateY(0) rotate(-2deg)} 50%{transform:translateY(-8px) rotate(2deg)} }
    @keyframes blinkCursor { 0%,49%{opacity:1} 50%,100%{opacity:0} }
    @keyframes waveBar { 0%,100%{transform:scaleY(0.35)} 50%{transform:scaleY(1)} }

    .land-nav-link {
      background: none; border: none; color: #4B5563; font-weight: 500;
      cursor: pointer; font-size: 14px; padding: 8px 14px; border-radius: 8px;
      font-family: inherit; transition: all 0.15s;
    }
    .land-nav-link:hover { color: #111827; background: #F3F4F6; }

    .land-btn-primary {
      background: #EF1D28; color: white; border: none; border-radius: 10px;
      padding: 12px 24px; font-weight: 700; font-size: 14px; cursor: pointer;
      font-family: inherit; display: inline-flex; align-items: center; gap: 7px;
      transition: all 0.2s; letter-spacing: -0.1px; box-shadow: 0 2px 8px rgba(239,29,40,0.2);
    }
    .land-btn-primary:hover {
      background: #D91620; transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(239,29,40,0.36);
    }
    .land-btn-primary:active { transform: translateY(0); box-shadow: 0 2px 8px rgba(239,29,40,0.2); }

    .land-btn-secondary {
      background: white; color: #374151; border: 1.5px solid #E5E7EB; border-radius: 10px;
      padding: 12px 22px; font-weight: 600; font-size: 14px; cursor: pointer;
      font-family: inherit; display: inline-flex; align-items: center; gap: 7px; transition: all 0.2s;
    }
    .land-btn-secondary:hover { background: #F9FAFB; border-color: #D1D5DB; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }

    .land-feature-card {
      background: white; border-radius: 16px; padding: 22px 20px;
      border: 1px solid #E5E7EB; transition: all 0.22s ease;
      position: relative; overflow: hidden; cursor: default;
    }
    .land-feature-card:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.09); transform: translateY(-3px); border-color: #D1D5DB; }
    .land-feature-card-accent { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; border-radius: 16px 0 0 16px; }

    .land-testimonial {
      background: #141E2E; border-radius: 18px; padding: 28px;
      border: 1px solid #1E2A40; transition: all 0.25s;
    }
    .land-testimonial:hover { border-color: rgba(239,29,40,0.25); transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,0.3); }

    .land-faq-item {
      background: white; border: 1.5px solid #E5E7EB; border-radius: 14px;
      overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s;
    }
    .land-faq-item:hover { border-color: #FECACA; box-shadow: 0 4px 16px rgba(239,29,40,0.06); }

    .land-logo-item {
      font-size: 15px; font-weight: 800; color: #CBD5E1; letter-spacing: -0.3px;
      transition: color 0.2s; cursor: default; white-space: nowrap;
    }
    .land-logo-item:hover { color: #64748B; }

    .land-stat-card {
      background: #0F1A2B; border-radius: 20px; padding: 32px 20px;
      border: 1px solid #1E2D45; text-align: center; transition: all 0.25s;
      position: relative; overflow: hidden;
    }
    .land-stat-card::after {
      content: ''; position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
      width: 60%; height: 1px;
      background: linear-gradient(90deg, transparent, rgba(239,29,40,0.35), transparent);
    }
    .land-stat-card:hover { border-color: rgba(239,29,40,0.25); background: #152030; transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,0.4); }

    .land-cta-badge {
      display: inline-flex; align-items: center; gap: 6px;
      border-radius: 24px; padding: 5px 14px; font-size: 11px; font-weight: 700;
      letter-spacing: 0.5px; text-transform: uppercase;
    }

    .land-section-eyebrow {
      display: inline-flex; align-items: center; gap: 7px;
      background: #FFF1F2; border: 1px solid #FECACA;
      border-radius: 24px; padding: 5px 14px; margin-bottom: 18px;
      font-size: 11px; font-weight: 700; color: #DC2626; letter-spacing: 0.5px;
      text-transform: uppercase;
    }

    .mobile-menu-btn { display: none !important; }
    .mobile-menu-overlay { display: none !important; }

    @media (max-width: 900px) {
      .marketing-header-nav { padding: 0 18px !important; }
      .marketing-header-links { display: none !important; }
      .marketing-header-actions { gap: 8px !important; }
      .mobile-menu-btn { display: flex !important; align-items: center; justify-content: center; background: none; border: 1.5px solid #E5E7EB; border-radius: 8px; padding: 7px 9px; cursor: pointer; color: #374151; font-size: 18px; line-height: 1; }
      .land-hero-grid { flex-direction: column !important; }
      .land-hero-grid h1 { font-size: 44px !important; letter-spacing: -1.5px !important; }
      .land-hero-visual { display: none !important; }
      .land-bento-grid { grid-template-columns: 1fr !important; }
      .land-bento-big { grid-column: span 1 !important; }
      .land-premium-grid { grid-template-columns: 1fr !important; }
      .land-ats-row { flex-direction: column !important; }
      .land-testimonials-grid { grid-template-columns: 1fr !important; }
      .land-footer-grid { flex-direction: column !important; gap: 32px !important; }
      .land-stats-grid { grid-template-columns: 1fr 1fr !important; }
    }

    @media (max-width: 640px) {
      .land-hero-grid h1 { font-size: 36px !important; letter-spacing: -1px !important; }
    }

    @media (max-width: 520px) {
      .marketing-header-nav { padding: 0 14px !important; }
      .marketing-header-actions > span { display: none !important; }
      .marketing-header-login { display: none !important; }
      .marketing-header-actions .land-btn-primary { padding: 8px 12px !important; font-size: 12px !important; border-radius: 8px !important; white-space: nowrap; }
      .marketing-header-logo-text { max-width: 132px; overflow: hidden; }
    }

    @media (max-width: 480px) {
      .land-stats-grid { grid-template-columns: 1fr !important; }
      .land-bento-grid { gap: 12px !important; }
    }

    /* ── Section padding responsive ── */
    @media (max-width: 900px) {
      .land-page section, .land-page footer {
        padding-left: 28px !important; padding-right: 28px !important;
      }
      .land-cta-card { padding: 60px 40px !important; }
      .land-bento-ai-row { padding: 36px 32px !important; }
    }
    @media (max-width: 640px) {
      .land-page section, .land-page footer {
        padding-left: 20px !important; padding-right: 20px !important;
      }
      .land-cta-card { padding: 36px 24px !important; }
      .land-cta-copy { flex: 0 0 100% !important; min-width: 0 !important; }
      .land-cta-perks { display: none !important; }
      .land-cta-h2 { font-size: 28px !important; letter-spacing: -0.5px !important; line-height: 1.2 !important; }
      .land-bento-ai-row { padding: 28px 22px !important; flex-direction: column !important; gap: 28px !important; }
      .land-bento-ai-left { flex: 0 0 auto !important; min-width: 0 !important; }
      .land-testimonials-grid { grid-template-columns: 1fr !important; }
      .land-page h2 { font-size: clamp(22px, 6vw, 38px) !important; }
    }
    @media (max-width: 480px) {
      .land-page section, .land-page footer {
        padding-left: 16px !important; padding-right: 16px !important;
      }
      .land-cta-card { padding: 28px 18px !important; }
      .land-cta-h2 { font-size: 24px !important; }
      .land-hero-grid h1 { font-size: 30px !important; letter-spacing: -0.5px !important; }
      .land-page h2 { font-size: clamp(20px, 5.5vw, 28px) !important; }
      .land-stat-card { padding: 24px 14px !important; }
    }

    .mobile-menu-overlay.open {
      display: flex !important;
      flex-direction: column;
      position: fixed; top: 64px; left: 0; right: 0; bottom: 0;
      background: white; z-index: 999;
      padding: 16px 20px; gap: 4px;
      overflow-y: auto;
      box-shadow: 0 8px 32px rgba(0,0,0,0.12);
      border-top: 1px solid #F3F4F6;
      animation: slideDown 0.18s ease;
    }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
    .mobile-nav-item {
      display: flex; align-items: center; padding: 13px 12px; border-radius: 10px;
      font-size: 15px; font-weight: 600; color: #1F2937; cursor: pointer;
      border: none; background: none; width: 100%; text-align: left; font-family: inherit;
      transition: background 0.12s;
    }
    .mobile-nav-item:hover { background: #F9FAFB; }
    .mobile-nav-divider { height: 1px; background: #F3F4F6; margin: 6px 0; }
    .mobile-nav-cta { width: 100%; padding: 13px; border-radius: 10px; font-size: 15px; font-weight: 700; text-align: center; cursor: pointer; border: none; font-family: inherit; margin-top: 6px; }
  `}</style>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uploadState, setUploadState] = useState('idle'); // idle | parsing | done | error
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = React.useRef(null);
  const landingCloseTimer = React.useRef(null);

  const landingOpenMenu = (name) => {
    if (landingCloseTimer.current) clearTimeout(landingCloseTimer.current);
    setOpenDropdown(name);
  };
  const landingScheduleClose = () => {
    landingCloseTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  usePageSEO({
    title: 'Free AI Resume Builder & ATS Resume Checker | MarsResume',
    description: "Create an ATS-friendly resume in minutes with MarsResume's AI Resume Builder. Get an ATS score, tailor your resume to any job, generate cover letters, practice interviews, and download in PDF or Word.",
    keywords: 'AI Resume Builder, ATS Resume Checker, Resume Builder, Resume Templates, AI Cover Letter Generator, Resume Optimization, Job Application, Resume Creator, Career Tools, Interview Preparation',
    robots: 'index, follow',
    ogDescription: 'Create ATS-friendly resumes with AI, optimize your resume for any job, generate cover letters, practice interviews, and land more interviews faster.',
    ogImageAlt: 'MarsResume AI Resume Builder',
    twitterDescription: 'Build ATS-friendly resumes with AI, generate cover letters, tailor resumes to jobs, and practice interviews—all in one platform.',
    canonical: '/',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const heroResumePreview = buildShowcaseResume({ id: 'harvard_standard' });

  const handleUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.type !== 'application/pdf') { setUploadError('Only PDF files are supported.'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('File must be under 5 MB.'); return; }
    setUploadState('parsing');
    setUploadError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      localStorage.setItem('mars_pending_file', base64);
      localStorage.setItem('mars_pending_filename', file.name);
      setUploadState('done');
      setTimeout(() => navigate(user ? '/dashboard' : '/signup?from=upload'), 500);
    } catch {
      setUploadState('error');
      setUploadError('Failed to read file. Please try again.');
    }
  };

  // If already logged in with a pending file, go to dashboard so the modal can handle it
  React.useEffect(() => {
    if (user && localStorage.getItem('mars_pending_file')) {
      navigate('/dashboard');
    }
  }, [user, navigate]);


  const stats = [
    { emoji: '🚀', value: 50000, decimals: 0, suffix: '+', label: 'Resumes Created' },
    { emoji: '📊', value: 94, decimals: 0, suffix: '%', label: 'Average ATS Success Rate' },
    { emoji: '🌍', value: 120, decimals: 0, suffix: '+', label: 'Countries' },
    { emoji: '⭐', value: 4.9, decimals: 1, suffix: '/5', label: 'User Rating' },
  ];

  const trustLogos = [
    { name: 'Google', color: '#4285F4' },
    { name: 'Microsoft', color: '#0078D4' },
    { name: 'Amazon', color: '#FF9900' },
    { name: 'Meta', color: '#0866FF' },
    { name: 'Apple', color: '#555555' },
    { name: 'Netflix', color: '#E50914' },
    { name: 'Spotify', color: '#1DB954' },
    { name: 'Stripe', color: '#635BFF' },
    { name: 'Shopify', color: '#95BF47' },
  ];

  const steps = [
    { n: '1', Icon: Layers, color: '#EF1D28', title: 'Choose Template', desc: 'Pick from 40+ ATS-friendly templates and make it your own.' },
    { n: '2', Icon: Sparkles, color: '#7C3AED', title: 'AI Writes Resume', desc: 'Describe your experience and AI writes a complete, polished resume in seconds.' },
    { n: '3', Icon: Target, color: '#059669', title: 'ATS Optimization', desc: 'We check keywords, formatting, and structure so it passes real hiring software.' },
    { n: '4', Icon: Download, color: '#2563EB', title: 'Download & Apply', desc: 'Export as PDF or Word and start applying with confidence.' },
  ];

  const beforeAfterMetrics = [
    { label: 'ATS Score', Icon: BarChart2, before: 34, after: 96, top: 2, offset: 34, duration: 5, delay: 0.2 },
    { label: 'Readability', Icon: Clock, before: 48, after: 92, top: 108, offset: 2, duration: 6.5, delay: 0.6 },
    { label: 'Formatting', Icon: FileText, before: 25, after: 98, top: 226, offset: 44, duration: 5.5, delay: 1 },
    { label: 'Keywords', Icon: Target, before: 30, after: 90, top: 336, offset: 6, duration: 7, delay: 1.4 },
  ];

  const bentoItems = [
    { key: 'resume', title: 'AI Resume', desc: 'Describe yourself in plain language — get a complete, ATS-ready resume in seconds.', Icon: Sparkles, gradient: 'linear-gradient(135deg,#EF1D28,#FF6B6B)', big: true, path: '/build/resume' },
    { key: 'ats', title: 'ATS Checker', desc: 'Instant score, plus exactly what to fix.', Icon: BarChart2, gradient: 'linear-gradient(135deg,#7C3AED,#A78BFA)', path: '/ats-checker' },
    { key: 'interview', title: 'Interview AI', desc: 'Practice questions with real-time AI coaching.', Icon: Mic, gradient: 'linear-gradient(135deg,#2563EB,#60A5FA)', path: '/ai-video-interview' },
    { key: 'video', title: 'Video Interview', desc: 'Mock interviews on camera, fully scored.', Icon: Video, gradient: 'linear-gradient(135deg,#4F46E5,#818CF8)', path: '/ai-video-interview' },
    { key: 'cover', title: 'Cover Letter', desc: 'Matching letters generated in one click.', Icon: Mail, gradient: 'linear-gradient(135deg,#059669,#34D399)', path: '/build/cover_letter' },
    { key: 'tracker', title: 'Job Tracker', desc: 'Track every application in one place.', Icon: Briefcase, gradient: 'linear-gradient(135deg,#D97706,#FBBF24)', path: '/signup' },
    { key: 'linkedin', title: 'LinkedIn Optimizer', desc: 'AI-scored profile fixes that get noticed.', Icon: Linkedin, gradient: 'linear-gradient(135deg,#0A66C2,#4C9AFF)', path: '/signup' },
  ];

  const STATIC_TESTIMONIALS = [
    { name: 'Ahmed R.', jobTitle: 'Software Engineer', company: 'NetSol Technologies', rating: 5, text: "MarsResume's AI wrote my entire resume in under 3 minutes. I pasted my experience and it came out sounding more professional than anything I'd written myself. Got an interview the next day.", initials: 'AR', color: '#4ADE80', atsBefore: 41, atsAfter: 93 },
    { name: 'Sara K.', jobTitle: 'Staff Nurse', company: 'NHS Trust, Manchester', rating: 5, text: "The ATS Checker showed my old CV was scoring 34%. MarsResume flagged every missing keyword and fixed the formatting. Two weeks later I had 3 interview calls. Nothing changed except my resume.", initials: 'SK', color: '#60A5FA', atsBefore: 34, atsAfter: 92 },
    { name: 'James L.', jobTitle: 'UI/UX Designer', company: 'Contour Software', rating: 5, text: "I tried 4 resume builders before MarsResume. The templates here actually look like a designer made them — not like a Word doc from 2012. Got compliments from two recruiters on the layout.", initials: 'JL', color: '#F472B6', atsBefore: 45, atsAfter: 90 },
    { name: 'Priya N.', jobTitle: 'Data Analyst', company: 'Nimbus Fintech', rating: 5, text: "My ATS score went from 38% to 91% in one session. MarsResume told me exactly what was wrong — missing skills section, wrong formatting, weak bullet points. Fixed everything in 20 minutes.", initials: 'PN', color: '#FBBF24', atsBefore: 38, atsAfter: 91 },
    { name: 'Daniel M.', jobTitle: 'Supply Chain Coordinator', company: 'DHL', rating: 5, text: "I'd been sending the same CV for months with zero response. MarsResume's job tailoring tool showed I was targeting the wrong keywords. Updated it, applied to 10 roles, got 4 replies in a week.", initials: 'DM', color: '#34D399', atsBefore: 39, atsAfter: 88 },
    { name: 'Sofia A.', jobTitle: 'HR Generalist', company: 'BrightPath Consulting', rating: 4, text: "The cover letter builder on MarsResume saved me so much time. I generated a draft, tweaked it in 5 minutes, and it sounded exactly like me. Hiring managers actually commented on it.", initials: 'SA', color: '#A78BFA', atsBefore: 50, atsAfter: 89 },
    { name: 'Tariq H.', jobTitle: 'Junior Accountant', company: 'Meridian Accounting Group', rating: 5, text: "As a fresh grad I had no idea how to structure a resume. MarsResume's AI asked me a few questions and built a complete professional resume. I went from zero callbacks to two offers.", initials: 'TH', color: '#FB923C', atsBefore: 30, atsAfter: 85 },
    { name: 'Carlos T.', jobTitle: 'Backend Developer', company: 'Arbisoft', rating: 5, text: "Used MarsResume to switch jobs twice this year. The AI tailoring feature rewrites my bullets to match each job description in seconds. What used to take me 2 hours now takes 10 minutes.", initials: 'CT', color: '#E879F9', atsBefore: 44, atsAfter: 94 },
    { name: 'Aisha N.', jobTitle: 'HR Coordinator', company: 'Aga Khan University Hospital', rating: 5, text: "I downloaded my resume as a PDF from MarsResume and a recruiter said it was the cleanest CV they'd seen that week. The Harvard template with custom colours looked completely polished.", initials: 'AN', color: '#60A5FA', atsBefore: 42, atsAfter: 90 },
  ];
  const T_COLORS = ['#4ADE80','#60A5FA','#F472B6','#FBBF24','#A78BFA','#34D399','#FB923C','#E879F9'];
  const [testimonials, setTestimonials] = React.useState(STATIC_TESTIMONIALS);
  React.useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    fetch(`${apiBase}/feedback/testimonials`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data.map((fb, i) => {
            const [jobTitle, company] = (fb.displayRole || '').split('·').map(s => s?.trim());
            return {
              name: fb.displayName || 'MarsResume User',
              jobTitle: jobTitle || 'MarsResume User',
              company: company || '',
              rating: fb.rating || 5,
              text: fb.message,
              initials: (fb.displayName || 'MU').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'MR',
              color: T_COLORS[i % T_COLORS.length],
              atsBefore: 30 + (i * 7) % 25,
              atsAfter: 86 + (i * 5) % 12,
            };
          }));
        }
      })
      .catch(() => {});
  }, []);

  const FAQ_CATEGORIES = [
    { id: 'resume-builder', label: 'Resume Builder' },
    { id: 'ats-checker', label: 'ATS Resume Checker' },
    { id: 'ai-interview', label: 'AI Interview' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'security', label: 'Security' },
  ];

  const faqs = [
    { category: 'resume-builder', popular: true,
      q: 'How does the AI resume builder work?',
      a: 'Describe your experience, skills, and target role in plain language. Our AI generates a complete, polished resume — professional summary, bullet points, and skills section — in under 30 seconds. You stay in full control and can edit, regenerate, or rearrange any section before downloading.' },
    { category: 'resume-builder', popular: true,
      q: 'What document types can I create?',
      a: 'Resumes, academic CVs, and cover letters — all from the same builder. Resumes are optimized for industry roles and ATS systems, CVs include extended sections for publications and research, and cover letters are tailored to a specific job and company.' },
    { category: 'resume-builder', popular: false,
      q: 'Can I switch templates after I start?',
      a: 'Yes. Switch between 40+ ATS-friendly templates at any time — just pick a new one from the sidebar and your content carries over instantly. No re-entering data required.' },
    { category: 'ats-checker', popular: true,
      q: 'How does the ATS Checker work?',
      a: 'Upload your resume or use one built on MarsResume. We scan it against 40+ criteria — keyword density, formatting, section structure, and file readability — and return a score out of 100 with specific fix recommendations for each issue found.' },
    { category: 'ats-checker', popular: true,
      q: 'Will my resume actually pass ATS filters?',
      a: 'Every MarsResume template is built to be machine-readable — no tables, graphics, or columns that break ATS parsing. Combined with the ATS Analyzer\'s live score and fix suggestions, your resume is built to clear automated filters, not just look good to a human.' },
    { category: 'ats-checker', popular: false,
      q: 'Can I tailor my resume to a specific job?',
      a: 'Yes. Paste any job description into the ATS Checker\'s Job Tailoring tool and it shows exactly which keywords and skills to add so your resume matches what that employer\'s ATS is scanning for.' },
    { category: 'ai-interview', popular: true,
      q: 'What is the AI Interview feature?',
      a: 'Practice real interview questions with an AI interviewer on camera. You get real-time feedback on eye contact, confidence, pacing, and answer quality — as many times as you need before the real thing.' },
    { category: 'ai-interview', popular: false,
      q: 'Do I need a webcam and microphone?',
      a: 'Yes — AI Interview runs right in your browser using your camera and microphone. No downloads or extra software required.' },
    { category: 'pricing', popular: true,
      q: 'Is MarsResume really free?',
      a: 'Yes. Building documents, accessing all templates, using AI writing tools, and running the ATS Analyzer are all free — no credit card required. The Pro plan adds unlimited documents, unlimited AI generation, and priority support for power users.' },
    { category: 'pricing', popular: false,
      q: "What's the difference between Free and Pro?",
      a: 'Free includes the full builder, all templates, limited AI generation, and PDF export. Pro removes the document limit, unlocks unlimited AI processing, adds ATS tailoring rewrites, and gives priority support. Both plans get the same templates and core features.' },
    { category: 'pricing', popular: false,
      q: 'Do you offer refunds?',
      a: 'Yes — refund requests within 7 days of your initial subscription purchase are reviewed on a case-by-case basis. Reach out to support@marsresume.com with your order details.' },
    { category: 'security', popular: true,
      q: 'Is my data secure?',
      a: 'Yes. All data is encrypted in transit over HTTPS and stored securely in the cloud. We never sell or share your personal information or resume content with third parties.' },
    { category: 'security', popular: false,
      q: 'Can I delete my account and my data?',
      a: 'Yes, at any time from your account settings. Deleting your account permanently removes your documents and personal data from our systems.' },
  ];

  const [faqCategory, setFaqCategory] = useState('all');
  const [faqShowAll, setFaqShowAll] = useState(false);

  const faqByCategory = faqCategory === 'all' ? faqs : faqs.filter(f => f.category === faqCategory);
  const visibleFaqs = faqShowAll ? faqByCategory : faqByCategory.filter(f => f.popular);
  const hasMoreFaqs = !faqShowAll && faqByCategory.length > visibleFaqs.length;


  const platformFeatureCards = [
    { label: 'AI Resume Builder', Icon: Sparkles, color: '#EF1D28', bg: 'linear-gradient(135deg,#FFF1F2,#FFE4E6)', side: 'left', top: -18, offset: 26, duration: 5, delay: 0.1 },
    { label: 'ATS Score', Icon: BarChart2, color: '#7C3AED', bg: 'linear-gradient(135deg,#F3E8FF,#E9D5FF)', side: 'left', top: 108, offset: 10, duration: 6.5, delay: 0.5 },
    { label: 'AI Interview', Icon: Mic, color: '#2563EB', bg: 'linear-gradient(135deg,#DBEAFE,#BFDBFE)', side: 'left', top: 234, offset: 28, duration: 5.5, delay: 0.9 },
    { label: 'Video Interview', Icon: Video, color: '#4F46E5', bg: 'linear-gradient(135deg,#E0E7FF,#C7D2FE)', side: 'left', top: 360, offset: 12, duration: 7, delay: 1.3 },
    { label: 'Job Tracker', Icon: Briefcase, color: '#D97706', bg: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', side: 'right', top: -18, offset: 26, duration: 6, delay: 0.3 },
    { label: 'Cover Letter', Icon: Mail, color: '#059669', bg: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', side: 'right', top: 108, offset: 10, duration: 5, delay: 0.7 },
    { label: 'LinkedIn Optimizer', Icon: Linkedin, color: '#0A66C2', bg: 'linear-gradient(135deg,#DBEAFE,#BAE6FD)', side: 'right', top: 234, offset: 28, duration: 6.5, delay: 1.1 },
    { label: 'Resume Analytics', Icon: TrendingUp, color: '#DB2777', bg: 'linear-gradient(135deg,#FCE7F3,#FBCFE8)', side: 'right', top: 360, offset: 12, duration: 5.5, delay: 1.5 },
  ];

  return (
    <div className="land-page" style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#FFFFFF', overflowX: 'hidden' }}>
      <LandingStyles />

      {/* ── Announcement Bar ── */}
      <div style={{ background: '#0D1117', padding: '9px 24px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <span style={{ background: '#EF1D28', color: 'white', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>NEW</span>
        <span style={{ fontSize: 13, color: '#8B949E' }}>
          <span style={{ color: '#E6EDF3', fontWeight: 600 }}>ATS Analyzer</span> & <span style={{ color: '#E6EDF3', fontWeight: 600 }}>AI Tailoring</span> now live —
        </span>
        <button onClick={() => goTo('/signup')} style={{ background: 'none', border: 'none', color: '#EF1D28', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Try free <ArrowRight size={12} />
        </button>
      </div>

      {/* ── Navbar ── */}
      <nav className="marketing-header-nav" style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #F3F4F6', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, position: 'sticky', top: 0, zIndex: 100 }}>

        {/* Logo */}
        <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
          <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 34, height: 34, objectFit: 'contain' }} />
          <div className="marketing-header-logo-text" style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#EF1D28', lineHeight: 1.1 }}>MarsResume</div>
            <div style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 400, lineHeight: 1 }}>Resume That Takes You to Mars</div>
          </div>
        </button>

        {/* Center nav — hidden on mobile */}
        <div className="marketing-header-links" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ position: 'relative' }} onMouseEnter={() => landingOpenMenu('features')} onMouseLeave={landingScheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'features' ? '#EF1D28' : undefined }}>
              Features <ChevronDown size={13} />
            </button>
            {openDropdown === 'features' && <FeaturesMegaMenu handleNav={(path) => { if (landingCloseTimer.current) clearTimeout(landingCloseTimer.current); if (path === '/signup') { navigate('/signup'); } else { navigate(path); } setOpenDropdown(null); }} />}
          </div>
          <div style={{ position: 'relative' }} onMouseEnter={() => landingOpenMenu('resume')} onMouseLeave={landingScheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'resume' ? '#EF1D28' : undefined }}>
              Resume <ChevronDown size={13} />
            </button>
            {openDropdown === 'resume' && <HeaderMegaMenu title="Explore Resumes" sections={RESUME_MEGA_SECTIONS} handleNav={(path) => { if (landingCloseTimer.current) clearTimeout(landingCloseTimer.current); navigate(path); setOpenDropdown(null); }} />}
          </div>
          <div style={{ position: 'relative' }} onMouseEnter={() => landingOpenMenu('coverletter')} onMouseLeave={landingScheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'coverletter' ? '#EF1D28' : undefined }}>
              Cover Letter <ChevronDown size={13} />
            </button>
            {openDropdown === 'coverletter' && <HeaderMegaMenu title="Explore Cover Letters" sections={COVER_LETTER_MEGA_SECTIONS} handleNav={(path) => { if (landingCloseTimer.current) clearTimeout(landingCloseTimer.current); navigate(path); setOpenDropdown(null); }} />}
          </div>
          <button className="land-nav-link" onClick={() => navigate('/ats-checker')}>ATS Checker</button>
          <button onClick={() => navigate('/ai-video-interview')} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#EF1D28,#FF5C3A)', color: 'white', border: 'none', borderRadius: 999, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 10px rgba(239,29,40,0.35)', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(239,29,40,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(239,29,40,0.35)'; }}>
            <Video size={14} strokeWidth={2.5} />
            AI Interview
          </button>
          <button className="land-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
        </div>

        {/* Right side CTAs */}
        <div className="marketing-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {user ? (
            <>
              <span style={{ color: '#9CA3AF', fontSize: 13, padding: '0 8px', fontWeight: 500 }}>Hi, {user.name?.split(' ')[0]} 👋</span>
              <button onClick={() => navigate('/dashboard')} className="land-btn-primary" style={{ padding: '9px 20px', fontSize: 13, borderRadius: 9 }}>Dashboard <ArrowRight size={14} /></button>
            </>
          ) : (
            <>
              <button className="marketing-header-login" onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 700, borderRadius: 9, border: '1.5px solid #EF1D28', background: 'white', color: '#EF1D28', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#FFF1F2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
              >Log In</button>
              <button onClick={() => navigate('/signup')} className="land-btn-primary" style={{ padding: '9px 20px', fontSize: 13, borderRadius: 9 }}>Create My Resume</button>
            </>
          )}
          {/* Hamburger — visible on mobile only */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Menu">
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu Overlay ── */}
      <div className={`mobile-menu-overlay${mobileMenuOpen ? ' open' : ''}`}>
        <button className="mobile-nav-item" onClick={() => { navigate('/features'); setMobileMenuOpen(false); }}>Features</button>
        <button className="mobile-nav-item" onClick={() => { navigate('/resume-templates'); setMobileMenuOpen(false); }}>Resume Templates</button>
        <button className="mobile-nav-item" onClick={() => { navigate('/cover-letter-templates'); setMobileMenuOpen(false); }}>Cover Letter Templates</button>
        <button className="mobile-nav-item" onClick={() => { navigate('/ats-checker'); setMobileMenuOpen(false); }}>ATS Checker</button>
        <button className="mobile-nav-item" onClick={() => { navigate('/ai-video-interview'); setMobileMenuOpen(false); }}>AI Video Interview</button>
        <button className="mobile-nav-item" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}>Pricing</button>
        <div className="mobile-nav-divider" />
        {user ? (
          <>
            <span style={{ padding: '8px 12px', fontSize: 13, color: '#6B7280' }}>Hi, {user.name?.split(' ')[0]} 👋</span>
            <button className="mobile-nav-cta" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} style={{ background: '#EF1D28', color: 'white' }}>Go to Dashboard</button>
          </>
        ) : (
          <>
            <button className="mobile-nav-cta" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} style={{ background: 'white', color: '#EF1D28', border: '1.5px solid #EF1D28' }}>Log In</button>
            <button className="mobile-nav-cta" onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} style={{ background: '#EF1D28', color: 'white' }}>Create My Resume</button>
          </>
        )}
      </div>

      {/* ── Hero ── */}
      <section style={{ background: 'linear-gradient(170deg,#FAFBFF 0%,#FFFFFF 60%,#FFF8F8 100%)', padding: '88px 48px 80px', position: 'relative', overflow: 'hidden', borderBottom: '1px solid #F3F4F6' }}>
        {/* Subtle grid pattern */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#E5E7EB 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.45, pointerEvents: 'none' }} />
        {/* Red glow */}
        <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 900, height: 480, background: 'radial-gradient(ellipse, rgba(239,29,40,0.07) 0%, transparent 68%)', pointerEvents: 'none' }} />
        {/* Blue accent glow */}
        <div style={{ position: 'absolute', top: '40%', right: '-5%', width: 500, height: 400, background: 'radial-gradient(ellipse, rgba(29,78,216,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="land-hero-grid" style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 64, position: 'relative' }}>
          {/* Left */}
          <div style={{ flex: '0 0 52%', animation: 'fadeUp 0.6s ease both' }}>
            <h1 style={{ margin: '0 0 22px', fontSize: 62, fontWeight: 900, color: '#0D1117', lineHeight: 1.04, letterSpacing: '-2.5px' }}>
              Your AI Career<br />
              <TypewriterWord style={{ color: '#EF1D28' }} words={[
                'Copilot.',
                'Resume AI.',
                'ATS Checker.',
                'Interview Coach.',
                'Job Tracker.',
                'LinkedIn Pro.',
              ]} />
            </h1>

            <p style={{ margin: '0 0 36px', fontSize: 18, color: '#6B7280', lineHeight: 1.72, maxWidth: 480 }}>
              One AI workspace for your whole job search — build an ATS-optimized resume, check your ATS score, practice with an AI interview coach, track every application, and polish your LinkedIn profile.
            </p>

            {/* ── Hero CTAs: Create + Upload on one line ── */}
            <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                {user ? (
                  <button onClick={() => navigate('/dashboard')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 30px', borderRadius: 12 }}>
                    Go to Dashboard <ArrowRight size={17} />
                  </button>
                ) : (
                  <button onClick={() => navigate('/build/resume')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 30px', borderRadius: 12 }}>
                    <Sparkles size={17} /> Start Free
                  </button>
                )}

                {uploadState === 'idle' || uploadState === 'error' ? (
                  <button onClick={handleUploadClick}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: '1.5px dashed #D1D5DB', borderRadius: 12, padding: '13px 20px', color: '#6B7280', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#EF1D28'; e.currentTarget.style.background = '#FFF1F2'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'none'; }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Upload Resume
                  </button>
                ) : uploadState === 'parsing' ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#F0F9FF', border: '1.5px solid #BAE6FD', borderRadius: 12, padding: '13px 20px', fontSize: 15, color: '#0369A1', fontWeight: 600 }}>
                    <div style={{ width: 16, height: 16, border: '2.5px solid #BAE6FD', borderTopColor: '#0284C7', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                    Reading file…
                  </div>
                ) : uploadState === 'done' ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: 12, padding: '13px 20px', fontSize: 15, color: '#15803D', fontWeight: 600 }}>
                    ✓ Ready! Redirecting…
                  </div>
                ) : null}
              </div>

              {uploadError && (
                <div style={{ fontSize: 13, color: '#DC2626', background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', maxWidth: 420 }}>
                  {uploadError}
                </div>
              )}
              {(uploadState === 'idle' || uploadState === 'error') && (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>PDF only · max 5 MB · AI extracts everything automatically</div>
              )}
            </div>

            {/* Social proof row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex' }}>
                  {[
                    { bg: '#4ADE80', letter: 'A' }, { bg: '#60A5FA', letter: 'S' },
                    { bg: '#F472B6', letter: 'J' }, { bg: '#FBBF24', letter: 'P' }, { bg: '#A78BFA', letter: 'C' }
                  ].map(({ bg, letter }, i) => (
                    <div key={i} style={{ width: 32, height: 32, borderRadius: '50%', background: bg, border: '2.5px solid white', marginLeft: i ? -8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 800, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                      {letter}
                    </div>
                  ))}
                </div>
                <div>
                  <StarRating />
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Trusted by <strong style={{ color: '#111827' }}>100+</strong> professionals</div>
                </div>
              </div>
              <div style={{ height: 28, width: 1, background: '#E5E7EB' }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Free forever', 'No card needed', 'PDF + Word'].map(t => (
                  <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', fontWeight: 500 }}>
                    <Check size={12} color="#059669" strokeWidth={3} /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Hero Visual */}
          <div className="land-hero-visual" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 620 }}>

            {/* Soft glow behind the card */}
            <div style={{ position: 'absolute', width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(239,29,40,0.09) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(29,78,216,0.08) 0%, transparent 70%)', bottom: '5%', right: '5%', pointerEvents: 'none' }} />

            {/* Card + floating badges — sized wrapper so badge offsets anchor to the card edges, not the wider flex container */}
            <div style={{ position: 'relative', width: 480 }}>

            {/* Browser frame — glassmorphism */}
            <div style={{ width: 480, background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', borderRadius: 20, boxShadow: '0 40px 90px rgba(15,23,42,0.16), 0 10px 28px rgba(15,23,42,0.08)', border: '1px solid rgba(255,255,255,0.8)', overflow: 'hidden', animation: 'heroFloat 6s ease-in-out infinite', position: 'relative', zIndex: 2 }}>

              {/* Browser chrome */}
              <div style={{ background: 'rgba(241,245,249,0.7)', borderBottom: '1px solid rgba(226,232,240,0.8)', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#FC5F5A' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#FDBA2F' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
                </div>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.85)', borderRadius: 7, padding: '5px 10px', marginLeft: 6, fontSize: 10, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 5, border: '1px solid #E2E8F0' }}>
                  <span style={{ fontSize: 9 }}>🔒</span>
                  <span style={{ fontWeight: 500 }}>marsresume.com/builder</span>
                </div>
              </div>

              {/* Resume preview — taller to show more content */}
              <div style={{ height: 520, overflow: 'hidden', position: 'relative', background: 'white' }}>
                <div style={{ transform: 'scale(0.666)', transformOrigin: 'top left', width: 721, pointerEvents: 'none' }}>
                  <ResumePreview data={heroResumePreview} />
                </div>
                {/* Fade at the bottom */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, transparent, white)', pointerEvents: 'none' }} />
              </div>
            </div>

            {/* Floating AI card — ATS Score (top right) */}
            <div style={{ position: 'absolute', top: 8, right: -24, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(187,247,208,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5s ease-in-out infinite 0.2s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircle2 size={17} color="#059669" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#065F46', lineHeight: 1.2 }}>ATS Score: 94%</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Highly optimized</div>
              </div>
            </div>

            {/* Floating AI card — Resume Suggestions (top left) */}
            <div style={{ position: 'absolute', top: 8, left: -30, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(233,213,255,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 6.5s ease-in-out infinite 0.6s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#F3E8FF,#E9D5FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Sparkles size={17} color="#7C3AED" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#5B21B6', lineHeight: 1.2 }}>Resume Suggestions</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>3 new improvements</div>
              </div>
            </div>

            {/* Floating AI card — Interview Ready (bottom right) */}
            <div style={{ position: 'absolute', bottom: 8, right: -26, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(191,219,254,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5.5s ease-in-out infinite 1s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#DBEAFE,#BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Video size={17} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>Interview Ready</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>AI mock interview scored</div>
              </div>
            </div>

            {/* Floating AI card — LinkedIn Optimized (bottom left) */}
            <div style={{ position: 'absolute', bottom: 12, left: -14, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(191,219,254,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 7s ease-in-out infinite 1.4s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#DBEAFE,#BAE6FD)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Linkedin size={17} color="#0A66C2" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#0A4C8C', lineHeight: 1.2 }}>LinkedIn Optimized</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Profile matched to resume</div>
              </div>
            </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── Trust & Statistics ── */}
      <section style={{ background: 'linear-gradient(180deg,#FAFBFF 0%,#FFFFFF 100%)', padding: '96px 48px 100px', borderBottom: '1px solid #F3F4F6' }}>
        <style>{`
          .land-trust2-logo {
            font-size: 18px; font-weight: 800; letter-spacing: -0.4px; white-space: nowrap;
            filter: grayscale(1) opacity(0.55); transition: filter 0.3s ease, transform 0.3s ease;
            cursor: default;
          }
          .land-trust2-logo:hover { filter: grayscale(0) opacity(1); transform: translateY(-2px); }

          .land-trust2-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; max-width: 1080px; margin: 0 auto; }
          .land-trust2-card {
            background: linear-gradient(160deg,#FFFFFF 0%,#FAFBFF 100%);
            border: 1px solid #EEF0F4; border-radius: 20px; padding: 36px 24px;
            text-align: center; box-shadow: 0 4px 20px rgba(15,23,42,0.05);
            transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, border-color 0.3s ease;
          }
          .land-trust2-card:hover { transform: translateY(-8px); box-shadow: 0 24px 48px rgba(15,23,42,0.12); border-color: #FBD3D5; }
          .land-trust2-emoji {
            width: 56px; height: 56px; border-radius: 16px; background: #FFF1F2; display: flex;
            align-items: center; justify-content: center; font-size: 26px; margin: 0 auto 18px;
          }

          @media (max-width: 860px) {
            .land-trust2-grid { grid-template-columns: 1fr 1fr; }
          }
          @media (max-width: 520px) {
            .land-trust2-grid { grid-template-columns: 1fr; }
            .land-trust2-logo { font-size: 16px; }
          }
        `}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div
            style={{ textAlign: 'center', marginBottom: 48 }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <h2 style={{ margin: '0 auto', fontSize: 34, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px', lineHeight: 1.2, maxWidth: 560 }}>
              Helping professionals land better jobs.
            </h2>
          </motion.div>

          <motion.div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, flexWrap: 'wrap', marginBottom: 72 }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          >
            {trustLogos.map(l => (
              <motion.span
                key={l.name}
                className="land-trust2-logo"
                style={{ color: l.color }}
                variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                {l.name}
              </motion.span>
            ))}
          </motion.div>

          <motion.div
            className="land-trust2-grid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          >
            {stats.map(({ emoji, value, decimals, suffix, label }) => (
              <motion.div
                key={label}
                className="land-trust2-card"
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="land-trust2-emoji">{emoji}</div>
                <div style={{ color: '#0D1117', fontWeight: 900, fontSize: 36, letterSpacing: '-1.2px', lineHeight: 1, marginBottom: 10 }}>
                  <AnimatedCounter value={value} decimals={decimals} suffix={suffix} />
                </div>
                <div style={{ color: '#6B7280', fontSize: 13.5, fontWeight: 600, lineHeight: 1.4 }}>{label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '100px 48px', background: '#FAFBFF' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 68 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 42, fontWeight: 900, color: '#0D1117', letterSpacing: '-1.2px', lineHeight: 1.1 }}>
              Everything you need to<br />land your dream job
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 520, lineHeight: 1.7 }}>
              Tools that used to cost hundreds per month — all in one place, free to start.
            </p>
          </div>

          <style>{`
            .land-premium-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
            .land-premium-card { background: white; border: 1.5px solid #E5E7EB; border-radius: 22px; overflow: hidden; display: flex; flex-direction: column; transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease, border-color 0.35s ease; }
            .land-premium-card:hover { transform: translateY(-8px); box-shadow: 0 28px 60px rgba(15,23,42,0.14); }
            .land-premium-illo { position: relative; height: 220px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
            .land-premium-float { transition: transform 0.35s ease; }
            .land-premium-card:hover .land-premium-float-1 { transform: translateY(-6px) rotate(-3deg); }
            .land-premium-card:hover .land-premium-float-2 { transform: translateY(6px) rotate(3deg); }
            .land-premium-body { padding: 28px 28px 30px; display: flex; flex-direction: column; flex: 1; }
            .land-premium-bullets { list-style: none; margin: 0 0 24px; padding: 0; display: flex; flex-direction: column; gap: 9px; }
            .land-premium-bullets li { display: flex; align-items: flex-start; gap: 8px; font-size: 13.5px; color: #374151; font-weight: 500; line-height: 1.4; }
            .land-premium-cta { margin-top: auto; display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: 11px; padding: 12px 20px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; border: none; transition: all 0.2s ease; }
          `}</style>

          {/* Three premium feature cards */}
          <div className="land-premium-grid">
            {/* Card 1 — AI Resume Builder */}
            <div className="land-premium-card" style={{ borderColor: '#FBD3D5' }}>
              <div className="land-premium-illo" style={{ background: 'linear-gradient(160deg,#FFF1F2 0%,#FFF7F2 60%,#FFFFFF 100%)' }}>
                <div style={{ width: 150, background: 'white', borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.12)', padding: '16px 14px', transform: 'rotate(-5deg)' }}>
                  <div style={{ width: 60, height: 8, background: '#0D1117', borderRadius: 4, marginBottom: 6 }} />
                  <div style={{ width: 40, height: 5, background: '#D1D5DB', borderRadius: 3, marginBottom: 14 }} />
                  {[100, 82, 92, 70].map((w, i) => (
                    <div key={i} style={{ height: 5, background: '#E5E7EB', borderRadius: 3, marginBottom: 6, width: `${w}%` }} />
                  ))}
                  <div style={{ width: 50, height: 6, background: '#EF1D28', borderRadius: 3, margin: '14px 0 6px' }} />
                  {[100, 88, 60].map((w, i) => (
                    <div key={i} style={{ height: 5, background: '#E5E7EB', borderRadius: 3, marginBottom: 6, width: `${w}%` }} />
                  ))}
                </div>
                <div className="land-premium-float land-premium-float-1" style={{ position: 'absolute', top: 26, right: 32, width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#EF1D28,#FF6B6B)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px rgba(239,29,40,0.35)' }}>
                  <Sparkles size={19} color="white" />
                </div>
                <div className="land-premium-float land-premium-float-2" style={{ position: 'absolute', bottom: 22, left: 28, background: 'white', borderRadius: 10, padding: '7px 12px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle2 size={14} color="#059669" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>ATS Ready</span>
                </div>
              </div>
              <div className="land-premium-body">
                <h3 style={{ color: '#111827', fontSize: 20, fontWeight: 900, margin: '0 0 10px', letterSpacing: '-0.4px' }}>AI Resume Builder</h3>
                <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.7, margin: '0 0 18px' }}>
                  Describe your experience in plain language — AI writes a complete, polished resume in under 30 seconds.
                </p>
                <ul className="land-premium-bullets">
                  {['Generates every section instantly', '40+ ATS-friendly templates', 'Export as PDF or Word'].map(b => (
                    <li key={b}><Check size={15} color="#EF1D28" strokeWidth={3} style={{ flexShrink: 0, marginTop: 1 }} />{b}</li>
                  ))}
                </ul>
                <button className="land-premium-cta" style={{ background: '#EF1D28', color: 'white', boxShadow: '0 4px 14px rgba(239,29,40,0.28)' }}
                  onClick={() => navigate('/build/resume')}
                  onMouseEnter={e => { e.currentTarget.style.background = '#D91620'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#EF1D28'; }}>
                  Build My Resume <ArrowRight size={15} />
                </button>
              </div>
            </div>

            {/* Card 2 — ATS Resume Analyzer */}
            <div className="land-premium-card" style={{ borderColor: '#E5E7EB' }}>
              <div className="land-premium-illo" style={{ background: 'linear-gradient(160deg,#F5F3FF 0%,#F3F0FF 60%,#FFFFFF 100%)' }}>
                <div style={{ position: 'relative', width: 140, height: 140 }}>
                  <svg width="140" height="140" viewBox="0 0 140 140">
                    <circle cx="70" cy="70" r="58" fill="none" stroke="#EDE9FE" strokeWidth="12" />
                    <circle cx="70" cy="70" r="58" fill="none" stroke="#7C3AED" strokeWidth="12" strokeLinecap="round"
                      strokeDasharray="364.4" strokeDashoffset="22" transform="rotate(-90 70 70)" />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: 30, fontWeight: 900, color: '#111827', letterSpacing: '-1px' }}>94%</div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#7C3AED', letterSpacing: 0.5 }}>ATS SCORE</div>
                  </div>
                </div>
                <div className="land-premium-float land-premium-float-1" style={{ position: 'absolute', top: 28, left: 26, background: 'white', borderRadius: 10, padding: '7px 12px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={13} color="#059669" strokeWidth={3} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>Keywords</span>
                </div>
                <div className="land-premium-float land-premium-float-2" style={{ position: 'absolute', bottom: 24, right: 24, background: 'white', borderRadius: 10, padding: '7px 12px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={13} color="#059669" strokeWidth={3} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>Formatting</span>
                </div>
              </div>
              <div className="land-premium-body">
                <h3 style={{ color: '#111827', fontSize: 20, fontWeight: 900, margin: '0 0 10px', letterSpacing: '-0.4px' }}>ATS Resume Analyzer</h3>
                <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.7, margin: '0 0 18px' }}>
                  Upload your resume and get an instant compatibility score with specific, actionable fixes.
                </p>
                <ul className="land-premium-bullets">
                  {['Keyword match analysis', 'Formatting & parsing checks', 'Section-by-section feedback'].map(b => (
                    <li key={b}><Check size={15} color="#7C3AED" strokeWidth={3} style={{ flexShrink: 0, marginTop: 1 }} />{b}</li>
                  ))}
                </ul>
                <button className="land-premium-cta" style={{ background: '#F5F3FF', color: '#6D28D9', border: '1.5px solid #DDD6FE' }}
                  onClick={() => navigate('/ats-checker')}
                  onMouseEnter={e => { e.currentTarget.style.background = '#EDE9FE'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#F5F3FF'; }}>
                  Check My Score <ArrowRight size={15} />
                </button>
              </div>
            </div>

            {/* Card 3 — AI Interview Practice */}
            <div className="land-premium-card" style={{ borderColor: '#E5E7EB' }}>
              <div className="land-premium-illo" style={{ background: 'linear-gradient(160deg,#EFF6FF 0%,#F0F5FF 60%,#FFFFFF 100%)' }}>
                <div style={{ width: 170, height: 120, background: '#0F172A', borderRadius: 16, boxShadow: '0 20px 40px rgba(15,23,42,0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 10, left: 10, width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#3B82F6,#60A5FA)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 17 }}>AI</div>
                  <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 16 }}>
                    {[6, 12, 8, 16, 9, 13, 7].map((h, i) => (
                      <div key={i} style={{ width: 3, height: h, background: '#60A5FA', borderRadius: 2, transformOrigin: 'bottom', animation: `waveBar 1.1s ease-in-out ${i * 0.08}s infinite` }} />
                    ))}
                  </div>
                </div>
                <div className="land-premium-float land-premium-float-1" style={{ position: 'absolute', top: 26, right: 28, background: 'white', borderRadius: 10, padding: '7px 12px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Video size={13} color="#2563EB" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>Live Feedback</span>
                </div>
                <div className="land-premium-float land-premium-float-2" style={{ position: 'absolute', bottom: 22, left: 26, background: 'white', borderRadius: 10, padding: '7px 12px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={13} color="#059669" strokeWidth={3} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>Scored</span>
                </div>
              </div>
              <div className="land-premium-body">
                <h3 style={{ color: '#111827', fontSize: 20, fontWeight: 900, margin: '0 0 10px', letterSpacing: '-0.4px' }}>AI Interview Practice</h3>
                <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.7, margin: '0 0 18px' }}>
                  Practice real interview questions with an AI coach and get instant feedback on every answer.
                </p>
                <ul className="land-premium-bullets">
                  {['Role-specific questions', 'Real-time AI feedback', 'Builds confidence before the real thing'].map(b => (
                    <li key={b}><Check size={15} color="#2563EB" strokeWidth={3} style={{ flexShrink: 0, marginTop: 1 }} />{b}</li>
                  ))}
                </ul>
                <button className="land-premium-cta" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1.5px solid #BFDBFE' }}
                  onClick={() => navigate('/ai-video-interview')}
                  onMouseEnter={e => { e.currentTarget.style.background = '#DBEAFE'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#EFF6FF'; }}>
                  Start Practicing <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ATS Analyzer ── */}
      <section style={{ padding: '100px 48px', background: 'white' }}>
        <style>{`
          .land-ats-checklist-item { border: 1px solid #E5E7EB; border-radius: 14px; padding: 16px 18px; transition: border-color 0.2s, box-shadow 0.2s; }
          .land-ats-checklist-item:hover { border-color: #A7F3D0; box-shadow: 0 4px 16px rgba(5,150,105,0.08); }
          .land-ats-bar-fill { transform-origin: left; transform: scaleX(0); transition: transform 1.1s cubic-bezier(0.22,1,0.36,1); }
          .land-ats-checklist-item.in-view .land-ats-bar-fill { transform: scaleX(1); }
        `}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 42, fontWeight: 900, color: '#0D1117', letterSpacing: '-1.2px', lineHeight: 1.1 }}>
              Know your ATS score<br />before you apply
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 520, lineHeight: 1.7 }}>
              Upload your resume and see exactly how applicant tracking systems read it — then fix what's holding you back.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 64, alignItems: 'center', flexWrap: 'wrap' }} className="land-ats-row">
            {/* Left — animated score ring with before/after */}
            <div style={{ flex: '0 0 auto', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <AnimatedScoreRing score={94} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 999, padding: '7px 16px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF' }}>Before</span>
                  <span style={{ fontSize: 15, fontWeight: 900, color: '#6B7280' }}>58%</span>
                </div>
                <ArrowRight size={16} color="#059669" strokeWidth={2.5} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 999, padding: '7px 16px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>After</span>
                  <span style={{ fontSize: 15, fontWeight: 900, color: '#047857' }}>94%</span>
                </div>
              </div>
            </div>

            {/* Right — checklist */}
            <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Keyword Match', Icon: Target, score: '92%', fill: 92 },
                { label: 'Formatting', Icon: FileText, score: 'Excellent', fill: 96 },
                { label: 'Missing Skills', Icon: Layers, score: '2 found', fill: 70 },
                { label: 'Readability', Icon: Clock, score: '88%', fill: 88 },
              ].map(({ label, Icon, score, fill }) => (
                <InViewFade key={label} className="land-ats-checklist-item">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={16} color="#059669" strokeWidth={2} />
                    </div>
                    <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#111827' }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#059669' }}>{score}</span>
                    <CheckCircle2 size={17} color="#059669" />
                  </div>
                  <div style={{ height: 5, background: '#ECFDF5', borderRadius: 3, overflow: 'hidden' }}>
                    <div className="land-ats-bar-fill" style={{ height: '100%', width: `${fill}%`, background: 'linear-gradient(90deg,#059669,#10B981)', borderRadius: 3 }} />
                  </div>
                </InViewFade>
              ))}

              <button onClick={() => navigate('/ats-checker')} style={{ marginTop: 10, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 9, background: '#059669', color: 'white', border: 'none', borderRadius: 12, padding: '14px 30px', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(5,150,105,0.28)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#047857'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Check Resume <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Interview ── */}
      <section style={{ padding: '100px 48px', background: 'linear-gradient(180deg,#F8FAFF 0%,#F1F5F9 100%)', overflow: 'hidden' }}>
        <style>{`
          .land-interview-float { transition: transform 0.35s ease; }
          @media (max-width: 760px) {
            .land-interview-visual { transform: scale(0.75); transform-origin: top center; margin-bottom: -95px !important; }
          }
          @media (max-width: 480px) {
            .land-interview-visual { transform: scale(0.52); margin-bottom: -190px !important; }
          }
        `}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 68 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 42, fontWeight: 900, color: '#0D1117', letterSpacing: '-1.2px', lineHeight: 1.1 }}>
              Practice interviews with<br />real-time AI feedback
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 540, lineHeight: 1.7 }}>
              Talk to an AI interviewer on camera and get scored on eye contact, confidence, pacing, and answer quality — as many times as you need.
            </p>
          </div>

          {/* Laptop + floating metric cards */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 56 }}>
          <div className="land-interview-visual" style={{ position: 'relative', width: 620, flexShrink: 0 }}>
            {/* Soft glow behind laptop */}
            <div style={{ position: 'absolute', width: 480, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 70%)', top: '30%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

            {/* Live AI Feedback pill — floating above the screen */}
            <div className="land-interview-float" style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', zIndex: 4, display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(239,29,40,0.25)', borderRadius: 20, padding: '7px 16px', boxShadow: '0 10px 28px rgba(0,0,0,0.10)', animation: 'heroFloat 5s ease-in-out infinite', whiteSpace: 'nowrap' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#EF1D28', animation: 'authPulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 12, fontWeight: 800, color: '#111827', letterSpacing: 0.2 }}>Live AI Feedback</span>
            </div>

            {/* Laptop screen */}
            <div style={{ position: 'relative', zIndex: 2, width: 560, margin: '0 auto', background: '#0F172A', borderRadius: '16px 16px 4px 4px', padding: 10, boxShadow: '0 40px 90px rgba(15,23,42,0.28), 0 10px 28px rgba(15,23,42,0.10)' }}>
              <div style={{ height: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#334155' }} />
              </div>
              <div style={{ borderRadius: 8, overflow: 'hidden', background: 'linear-gradient(160deg,#111C33 0%,#0B1223 100%)', height: 340, position: 'relative' }}>
                {/* Top bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF4444', animation: 'authPulse 1.6s ease-in-out infinite' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.3 }}>AI INTERVIEW</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#64748B' }}>12:34</span>
                </div>

                {/* AI avatar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, height: 190 }}>
                  <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'linear-gradient(135deg,#3B82F6,#60A5FA)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 24, boxShadow: '0 12px 32px rgba(59,130,246,0.4)' }}>AI</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 22 }}>
                    {[8, 16, 11, 22, 13, 18, 9, 15].map((h, i) => (
                      <div key={i} style={{ width: 4, height: h, background: '#60A5FA', borderRadius: 2, transformOrigin: 'bottom', animation: `waveBar 1.1s ease-in-out ${i * 0.08}s infinite` }} />
                    ))}
                  </div>
                </div>

                {/* Self-view PiP */}
                <div style={{ position: 'absolute', bottom: 54, right: 16, width: 66, height: 48, borderRadius: 8, background: 'linear-gradient(160deg,#1E293B,#0F172A)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#334155' }} />
                </div>

                {/* Question caption bar */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(15,23,42,0.85)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Mic size={13} color="#60A5FA" />
                  <span style={{ fontSize: 12, color: '#CBD5E1', fontWeight: 500 }}>"Tell me about a challenging project you led…"</span>
                </div>
              </div>
            </div>

            {/* Laptop base */}
            <div style={{ position: 'relative', zIndex: 1, width: 620, height: 16, margin: '0 auto', background: 'linear-gradient(180deg,#E2E8F0,#CBD5E1)', borderRadius: '0 0 10px 10px', boxShadow: '0 10px 24px rgba(15,23,42,0.15)' }}>
              <div style={{ width: 80, height: 5, background: '#94A3B8', borderRadius: 3, margin: '0 auto' }} />
            </div>

            {/* Floating metric — Eye Contact (top-left) */}
            <div className="land-interview-float" style={{ position: 'absolute', top: -10, left: -36, zIndex: 4, background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(191,219,254,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5s ease-in-out infinite 0.2s' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#DBEAFE,#BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Eye size={17} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>Eye Contact</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>96% on camera</div>
              </div>
            </div>

            {/* Floating metric — Confidence (top-right) */}
            <div className="land-interview-float" style={{ position: 'absolute', top: -10, right: -36, zIndex: 4, background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(233,213,255,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 6.5s ease-in-out infinite 0.6s' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#F3E8FF,#E9D5FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Award size={17} color="#7C3AED" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#5B21B6', lineHeight: 1.2 }}>Confidence</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>91 / 100</div>
              </div>
            </div>

            {/* Floating metric — Speech Speed (bottom-left) */}
            <div className="land-interview-float" style={{ position: 'absolute', bottom: -22, left: -30, zIndex: 4, background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(253,230,138,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5.5s ease-in-out infinite 1s' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Mic size={17} color="#D97706" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#92400E', lineHeight: 1.2 }}>Speech Speed</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Ideal pace</div>
              </div>
            </div>

            {/* Floating metric — Answer Quality (bottom-right) */}
            <div className="land-interview-float" style={{ position: 'absolute', bottom: -22, right: -30, zIndex: 4, background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderRadius: 14, padding: '11px 15px', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', border: '1px solid rgba(167,243,208,0.8)', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 7s ease-in-out infinite 1.4s' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircle2 size={17} color="#059669" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#065F46', lineHeight: 1.2 }}>Answer Quality</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>9.2 / 10 score</div>
              </div>
            </div>
          </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button onClick={() => navigate('/ai-video-interview')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12 }}>
              <Video size={17} /> Practice Interview
            </button>
            <div style={{ marginTop: 14, fontSize: 12, color: '#94A3B8' }}>Works right in your browser — camera & mic required</div>
          </div>
        </div>
      </section>

      {/* ── Bento Grid ── */}
      <section style={{ padding: '100px 48px', background: '#FAFBFF' }}>
        <style>{`
          .land-bento2-grid { display: grid; grid-template-columns: repeat(4,1fr); grid-auto-rows: 172px; gap: 18px; }
          .land-bento2-card {
            position: relative; border-radius: 20px; padding: 24px; overflow: hidden; cursor: pointer;
            display: flex; flex-direction: column; justify-content: space-between; color: white; border: none;
            text-align: left; font-family: inherit; box-shadow: 0 8px 24px rgba(0,0,0,0.10);
            transition: transform 0.32s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.32s ease;
          }
          .land-bento2-card:hover { transform: translateY(-6px) scale(1.015); box-shadow: 0 22px 46px rgba(0,0,0,0.22); }
          .land-bento2-card:hover .land-bento2-icon { transform: scale(1.14) rotate(-6deg); }
          .land-bento2-card:hover .land-bento2-arrow { opacity: 1; transform: translate(0,0); }
          .land-bento2-glow { position: absolute; inset: 0; background: radial-gradient(circle at 28% 18%, rgba(255,255,255,0.28), transparent 60%); pointer-events: none; }
          .land-bento2-bg-icon { position: absolute; opacity: 0.16; pointer-events: none; z-index: 0; }
          .land-bento2-icon {
            width: 46px; height: 46px; border-radius: 13px; background: rgba(255,255,255,0.2);
            backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center;
            transition: transform 0.32s ease; position: relative; z-index: 1;
          }
          .land-bento2-card.big .land-bento2-icon { width: 58px; height: 58px; border-radius: 16px; }
          .land-bento2-arrow {
            position: absolute; top: 20px; right: 20px; width: 30px; height: 30px; border-radius: 50%;
            background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center;
            opacity: 0; transform: translate(-4px,4px); transition: all 0.32s ease; z-index: 1;
          }
          .land-bento2-title { font-size: 16px; font-weight: 800; letter-spacing: -0.2px; position: relative; z-index: 1; }
          .land-bento2-card.big .land-bento2-title { font-size: 24px; }
          .land-bento2-desc { font-size: 12.5px; color: rgba(255,255,255,0.88); line-height: 1.5; margin-top: 4px; position: relative; z-index: 1; }
          .land-bento2-card.big .land-bento2-desc { font-size: 14.5px; max-width: 320px; }
          @media (max-width: 900px) {
            .land-bento2-grid { grid-template-columns: repeat(2,1fr); grid-auto-rows: 160px; }
            .land-bento2-card.big { grid-column: 1 / 3 !important; grid-row: auto !important; height: 200px; }
            .land-bento2-card:not(.big) { grid-column: auto !important; grid-row: auto !important; }
          }
        `}</style>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 42, fontWeight: 900, color: '#0D1117', letterSpacing: '-1.2px', lineHeight: 1.1 }}>
              Your complete career toolkit
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 520, lineHeight: 1.7 }}>
              Seven AI-powered tools working together to get you hired faster — all in one place.
            </p>
          </div>

          <div className="land-bento2-grid">
            {bentoItems.map(({ key, title, desc, Icon, gradient, big, path }, i) => {
              const isWide = i === 1 || i === 6;
              const bgIconStyle = big
                ? { right: -22, bottom: -26 }
                : isWide
                ? { right: 26, top: '50%', transform: 'translateY(-50%) rotate(-8deg)' }
                : { right: -16, bottom: -16 };
              return (
                <button key={key} type="button" onClick={() => goTo(path)}
                  className={`land-bento2-card${big ? ' big' : ''}`}
                  style={{
                    background: gradient,
                    gridColumn: big ? '1 / 3' : i === 1 ? '3 / 5' : i === 6 ? '3 / 5' : 'auto',
                    gridRow: big ? '1 / 3' : i === 1 ? '1 / 2' : i === 6 ? '3 / 4' : 'auto',
                  }}>
                  <div className="land-bento2-glow" />
                  <Icon className="land-bento2-bg-icon" style={bgIconStyle} size={big ? 210 : isWide ? 130 : 92} color="white" strokeWidth={1.25} />
                  <div className="land-bento2-arrow"><ArrowRight size={14} color="white" /></div>
                  <div className="land-bento2-icon"><Icon size={big ? 26 : 20} color="white" /></div>
                  <div>
                    <div className="land-bento2-title">{title}</div>
                    <div className="land-bento2-desc">{desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Template Showcase ── */}
      <TemplateShowcaseSection onBrowse={() => navigate('/templates')}
        onUseTemplate={(templateId) => navigate(user ? `/builder/resume/new?template=${templateId}` : `/build/resume?template=${templateId}`)} />

      {/* ── How It Works ── */}
      <section style={{ padding: '100px 48px', background: 'white' }}>
        <style>{`
          .land-timeline-line-fill {
            position: absolute; top: 27px; left: 12.5%; right: 12.5%; height: 3px; border-radius: 2px;
            background: linear-gradient(90deg,#EF1D28,#7C3AED,#059669,#2563EB);
            transform-origin: left; transform: scaleX(0);
            transition: transform 1.8s cubic-bezier(0.22,1,0.36,1);
            z-index: 1;
          }
          .land-timeline-line-fill.in-view { transform: scaleX(1); }
          .land-timeline-step:hover .land-timeline-icon-circle { transform: translateY(-4px) scale(1.05); }
          .land-timeline-icon-circle { transition: transform 0.3s ease; }
          @media (max-width: 900px) {
            .land-timeline-steps { flex-direction: column !important; gap: 28px !important; }
            .land-timeline-step { text-align: left !important; flex-direction: row !important; align-items: flex-start !important; gap: 16px !important; }
            .land-timeline-line-track, .land-timeline-line-fill { display: none !important; }
          }
        `}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 76 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 40, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px' }}>
              Resume ready in 4 steps
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 400, lineHeight: 1.65 }}>
              No experience writing resumes? No problem.
            </p>
          </div>

          <div style={{ position: 'relative' }}>
            <div className="land-timeline-line-track" style={{ position: 'absolute', top: 27, left: '12.5%', right: '12.5%', height: 3, borderRadius: 2, background: '#E5E7EB', zIndex: 0 }} />
            <InViewFade className="land-timeline-line-fill" />

            <div className="land-timeline-steps" style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 20 }}>
              {steps.map(({ n, Icon, color, title, desc }, i) => (
                <div key={i} className="land-timeline-step" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <div className="land-timeline-icon-circle" style={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', background: 'white', border: `2.5px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, boxShadow: `0 8px 20px ${color}30` }}>
                    <Icon size={24} color={color} strokeWidth={2} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>Step {n}</div>
                  <div style={{ fontWeight: 800, fontSize: 17, color: '#111827', marginBottom: 10 }}>{title}</div>
                  <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.75, maxWidth: 220 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 60 }}>
            <button onClick={() => goTo('/signup')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12 }}>
              <Sparkles size={17} /> Start for free
            </button>
          </div>
        </div>
      </section>

      {/* ── Before / After ── */}
      <section style={{ padding: '100px 48px', background: '#FAFBFF' }}>
        <style>{`
          .land-cmp-cards { display: flex; align-items: center; justify-content: center; gap: 32px; flex-wrap: wrap; margin-bottom: 72px; }
          .land-cmp-card { width: 320px; background: white; border-radius: 20px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04); }
          .land-cmp-card.old { border: 1.5px solid #FECACA; }
          .land-cmp-card.new { border: 1.5px solid #A7F3D0; }
          .land-cmp-thumb { position: relative; height: 380px; overflow: hidden; }
          .land-cmp-fade { position: absolute; bottom: 0; left: 0; right: 0; height: 60px; background: linear-gradient(to bottom, transparent, white); pointer-events: none; }
          .land-cmp-arrow {
            width: 48px; height: 48px; border-radius: 50%; background: white; border: 1.5px solid #E5E7EB;
            display: flex; align-items: center; justify-content: center; flex-shrink: 0;
            box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          }
          .land-cmp-card-wrap { position: relative; }
          .land-cmp-side-tag {
            position: absolute; z-index: 3;
            display: flex; align-items: center; gap: 7px; background: white; border-radius: 12px;
            padding: 8px 13px; box-shadow: 0 8px 20px rgba(0,0,0,0.09); font-size: 12px; font-weight: 700;
            white-space: nowrap; color: #374151;
          }
          .land-cmp-side-tag.old { border: 1px solid #FECACA; }
          .land-cmp-side-tag.new { border: 1px solid #A7F3D0; }
          .land-cmp-side-tag .val { font-weight: 900; }
          @media (max-width: 1200px) {
            .land-cmp-side-tag { display: none; }
          }
          @media (max-width: 760px) {
            .land-cmp-arrow { transform: rotate(90deg); }
          }
        `}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 40, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px' }}>
              See the MarsResume difference
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 480, lineHeight: 1.7 }}>
              Same experience, same skills — rewritten and reformatted so hiring software and recruiters actually notice it.
            </p>
          </div>

          {/* Two resume mockups */}
          <div className="land-cmp-cards">
            <div className="land-cmp-card-wrap">
              {beforeAfterMetrics.map(({ label, Icon, before, top, offset, duration, delay }) => (
                <div key={label} className="land-cmp-side-tag old"
                  style={{ top, right: `calc(100% + ${offset}px)`, animation: `heroFloat ${duration}s ease-in-out infinite ${delay}s` }}>
                  <Icon size={13} color="#B91C1C" />
                  {label} <span className="val" style={{ color: '#B91C1C' }}>{before}%</span>
                </div>
              ))}

              <div className="land-cmp-card old">
                <div className="land-cmp-thumb" style={{ background: '#FDFDFB', padding: '22px 22px 0' }}>
                  <div style={{ textAlign: 'center', fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>John Smith</div>
                  <div style={{ textAlign: 'center', fontFamily: 'Georgia, serif', fontSize: 9, color: '#6B7280', marginBottom: 16 }}>123 Main St, Anytown, USA &nbsp;|&nbsp; (555) 555-5555 &nbsp;|&nbsp; john.smith@email.com</div>

                  <div style={{ fontFamily: 'Georgia, serif', fontSize: 11, fontWeight: 700, textDecoration: 'underline', color: '#374151', marginBottom: 6 }}>Objective</div>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: 10, color: '#4B5563', lineHeight: 1.55, margin: '0 0 14px' }}>
                    To obtain a challenging position in a reputable company where I can utilize my skills and experience to contribute to the growth of the organization.
                  </p>

                  <div style={{ fontFamily: 'Georgia, serif', fontSize: 11, fontWeight: 700, textDecoration: 'underline', color: '#374151', marginBottom: 6 }}>Experience</div>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: 10, color: '#4B5563', lineHeight: 1.55, margin: '0 0 10px' }}>
                    Company ABC, Sales Associate, 2018–2020. Responsible for helping customers, managing inventory, and other duties as assigned by management.
                  </p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: 10, color: '#4B5563', lineHeight: 1.55, margin: '0 0 14px' }}>
                    Company XYZ, Assistant Manager, 2016–2018. Worked with team to accomplish goals and objectives set forth by upper management.
                  </p>

                  <div style={{ fontFamily: 'Georgia, serif', fontSize: 11, fontWeight: 700, textDecoration: 'underline', color: '#374151', marginBottom: 6 }}>Education</div>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: 10, color: '#4B5563', lineHeight: 1.55, margin: 0 }}>
                    State University, Bachelor of Arts, 2016
                  </p>
                  <div className="land-cmp-fade" />
                </div>
              </div>
            </div>

            <div className="land-cmp-arrow"><ArrowRight size={20} color="#9CA3AF" /></div>

            <div className="land-cmp-card-wrap">
              <div className="land-cmp-card new">
                <div className="land-cmp-thumb">
                  <div style={{ transform: 'scale(0.44)', transformOrigin: 'top left', width: `${100 / 0.44}%`, pointerEvents: 'none' }}>
                    <ResumePreview data={buildShowcaseResume({ id: 'harvard_standard' })} id="cmp-after-resume" />
                  </div>
                  <div className="land-cmp-fade" />
                </div>
              </div>

              {beforeAfterMetrics.map(({ label, Icon, after, top, offset, duration, delay }) => (
                <div key={label} className="land-cmp-side-tag new"
                  style={{ top, left: `calc(100% + ${offset}px)`, animation: `heroFloat ${duration}s ease-in-out infinite ${delay}s` }}>
                  <Icon size={13} color="#047857" />
                  {label} <span className="val" style={{ color: '#047857' }}>{after}%</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 56 }}>
            <button onClick={() => goTo('/build/resume')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12 }}>
              <Sparkles size={17} /> Fix My Resume
            </button>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={{ padding: '96px 0', background: '#F3F4F0', overflow: 'hidden' }}>
        <style>{`
          @keyframes marqueeScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .land-marquee-track { display: flex; gap: 20px; width: max-content; animation: marqueeScroll 32s linear infinite; }
          .land-marquee-track:hover { animation-play-state: paused; }
          .land-tcard { background: white; border: 1px solid #E5E7EB; border-radius: 16px; padding: 28px 26px; width: 340px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.05); display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
        `}</style>

        {/* Header */}
        <div style={{ textAlign: 'center', padding: '0 48px', marginBottom: 56 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 14 }}>From the MarsResume Community</div>
          <h2 style={{ margin: '0 0 16px', fontSize: 42, fontWeight: 900, color: '#111827', letterSpacing: '-1.2px', lineHeight: 1.1 }}>
            Real people. Real resumes.<br />Real offers.
          </h2>
          <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 540, lineHeight: 1.72 }}>
            From AI-built resumes to ATS fixes to job-tailored cover letters — here's what MarsResume users say after landing their next role.
          </p>
        </div>

        {/* Marquee track */}
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Left fade */}
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(to right, #F3F4F0, transparent)', zIndex: 2, pointerEvents: 'none' }} />
          {/* Right fade */}
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(to left, #F3F4F0, transparent)', zIndex: 2, pointerEvents: 'none' }} />

          <div className="land-marquee-track" style={{ paddingLeft: 40 }}>
            {[...testimonials, ...testimonials].map(({ name, jobTitle, company, rating, text, initials, color, atsBefore, atsAfter }, i) => (
              <div key={i} className="land-tcard">
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <StarRating n={rating} color="#10B981" emptyColor="#D1FAE5" total={5} />
                    <svg width="24" height="18" viewBox="0 0 32 24" fill="none"><path d="M0 24V15C0 10.5 1.5 6.75 4.5 3.75C7.5 0.75 11.5 0 16 0v4c-3 0-5.5 1-7.5 3S5.5 11.5 5.5 14.5V16H12V24H0ZM18 24V15c0-4.5 1.5-8.25 4.5-11.25C25.5 0.75 29.5 0 34 0v4c-3 0-5.5 1-7.5 3S23.5 11.5 23.5 14.5V16H30V24H18Z" fill="#D1FAE5"/></svg>
                  </div>
                  <p style={{ margin: '0 0 16px', fontSize: 14, color: '#374151', lineHeight: 1.82 }}>{text}</p>
                  {Number.isFinite(atsBefore) && Number.isFinite(atsAfter) && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, color: '#047857', marginBottom: 16 }}>
                      <BarChart2 size={12} color="#059669" />
                      ATS Score <span style={{ color: '#9CA3AF', fontWeight: 600 }}>{atsBefore}%</span> → <span style={{ fontWeight: 900 }}>{atsAfter}%</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111827', fontWeight: 900, fontSize: 13, flexShrink: 0 }}>
                    {initials}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{jobTitle}{company ? ` · ${company}` : ''}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginTop: 52, padding: '0 48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 32 }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: '#111827', letterSpacing: '-0.5px' }}>4.9/5</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>User Rating</div>
              <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                {[...Array(5)].map((_, i) => <Star key={i} size={11} fill="#10B981" color="#10B981" />)}
              </div>
            </div>
          </div>
          <div style={{ width: 1, height: 36, background: '#D1D5DB', marginRight: 32 }} />
          <div style={{ fontSize: 14, color: '#6B7280', fontWeight: 500 }}>Job seekers landing roles worldwide</div>
        </div>
      </section>

      {/* ── One Platform ── */}
      <section style={{ padding: '110px 48px', background: 'linear-gradient(180deg,#FFFFFF 0%,#FAFBFF 100%)', overflow: 'hidden' }}>
        <style>{`
          .land-plat-card {
            position: absolute; z-index: 3; display: flex; align-items: center; gap: 10px;
            background: rgba(255,255,255,0.75); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
            border: 1px solid rgba(255,255,255,0.85); border-radius: 16px; padding: 11px 16px;
            box-shadow: 0 16px 36px rgba(15,23,42,0.12); transition: transform 0.25s ease, box-shadow 0.25s ease;
            cursor: default; white-space: nowrap;
          }
          .land-plat-card:hover { transform: translateY(-4px) scale(1.06); box-shadow: 0 22px 46px rgba(15,23,42,0.18); }
          @media (max-width: 1150px) {
            .land-plat-card { display: none; }
          }
          @media (max-width: 620px) {
            .land-plat-right-col { min-width: 0 !important; width: 100%; }
            .land-plat-dashboard-wrap { transform: scale(0.8); transform-origin: top center; margin-bottom: -95px !important; }
          }
          @media (max-width: 420px) {
            .land-plat-dashboard-wrap { transform: scale(0.64); margin-bottom: -175px !important; }
          }
        `}</style>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 90, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Left: copy */}
            <div style={{ flex: '0 0 380px', minWidth: 300 }}>
              <h2 style={{ margin: '0 0 16px', fontSize: 38, fontWeight: 900, color: '#0D1117', letterSpacing: '-0.8px', lineHeight: 1.15 }}>
                Everything You Need<br />to Get Hired.
              </h2>
              <p style={{ margin: '0 0 32px', color: '#6B7280', fontSize: 15, lineHeight: 1.8 }}>
                No more juggling five different tools. MarsResume brings your resume builder, ATS optimization, interview preparation, LinkedIn optimization, cover letters, and job tracking together in one place.
              </p>
              <button onClick={() => goTo('/signup')} className="land-btn-primary" style={{ fontSize: 15, padding: '13px 28px', borderRadius: 11 }}>
                Start Building Free <ArrowRight size={16} />
              </button>
            </div>

            {/* Right: dashboard mockup + floating feature cards */}
            <div className="land-plat-right-col" style={{ flex: 1, minWidth: 480, display: 'flex', justifyContent: 'center' }}>
              <div className="land-plat-dashboard-wrap" style={{ position: 'relative', width: 460 }}>
                {/* Soft glow */}
                <div style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(239,29,40,0.10) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)', bottom: '0%', right: '0%', pointerEvents: 'none' }} />

                {/* Dashboard mockup */}
                <div style={{ position: 'relative', zIndex: 2, background: 'white', borderRadius: 20, boxShadow: '0 40px 100px rgba(15,23,42,0.16), 0 10px 30px rgba(15,23,42,0.08)', border: '1px solid #E5E7EB', overflow: 'hidden', animation: 'heroFloat 7s ease-in-out infinite' }}>
                  {/* Top bar */}
                  <div style={{ background: '#F8FAFC', borderBottom: '1px solid #E5E7EB', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#FC5F5A' }} />
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#FDBA2F' }} />
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#28C840' }} />
                    </div>
                    <div style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#475569' }}>MarsResume Dashboard</div>
                    <div style={{ marginLeft: 'auto', width: 24, height: 24, borderRadius: '50%', background: '#EF1D28', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>SC</div>
                  </div>

                  <div style={{ display: 'flex' }}>
                    {/* Sidebar */}
                    <div style={{ width: 56, background: '#0F172A', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '20px 0', flexShrink: 0 }}>
                      {[
                        { Icon: Sparkles, active: true },
                        { Icon: BarChart2, active: false },
                        { Icon: Mic, active: false },
                        { Icon: Linkedin, active: false },
                        { Icon: Briefcase, active: false },
                      ].map(({ Icon, active }, i) => (
                        <div key={i} style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? '#EF1D28' : 'rgba(255,255,255,0.06)' }}>
                          <Icon size={15} color={active ? 'white' : '#94A3B8'} />
                        </div>
                      ))}
                    </div>

                    {/* Main content */}
                    <div style={{ flex: 1, padding: 20, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 3 }}>Welcome back, Sarah</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 16 }}>Here's your career progress</div>

                      {/* Stat row */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        {[
                          { label: 'Documents', value: '3', color: '#EF1D28' },
                          { label: 'ATS Score', value: '94%', color: '#059669' },
                          { label: 'Applications', value: '12', color: '#7C3AED' },
                        ].map(s => (
                          <div key={s.label} style={{ flex: 1, background: '#FAFBFF', border: '1px solid #F1F5F9', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
                            <div style={{ fontSize: 16, fontWeight: 900, color: s.color, letterSpacing: -0.5 }}>{s.value}</div>
                            <div style={{ fontSize: 8.5, color: '#94A3B8', fontWeight: 700, marginTop: 2 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Mini chart */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 42, marginBottom: 16, padding: '0 2px' }}>
                        {[40, 55, 35, 70, 50, 85, 65].map((h, i) => (
                          <div key={i} style={{ flex: 1, height: `${h}%`, background: 'linear-gradient(180deg,#FCA5A5,#EF1D28)', borderRadius: 3 }} />
                        ))}
                      </div>

                      {/* Document rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                          { title: 'Product Manager Resume', color: '#EF1D28' },
                          { title: 'Cover Letter — TechCorp', color: '#059669' },
                        ].map(d => (
                          <div key={d.title} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #F1F5F9', borderRadius: 10, background: '#FAFBFF' }}>
                            <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: d.color, flexShrink: 0 }} />
                            <FileText size={12} color="#94A3B8" style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating feature cards */}
                {platformFeatureCards.map(({ label, Icon, color, bg, side, top, offset, duration, delay }) => (
                  <div key={label} className="land-plat-card"
                    style={{
                      top,
                      ...(side === 'left' ? { right: `calc(100% + ${offset}px)` } : { left: `calc(100% + ${offset}px)` }),
                      animation: `heroFloat ${duration}s ease-in-out infinite ${delay}s`,
                    }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={17} color={color} />
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ background: '#FAFBFF', padding: '100px 48px' }}>
        <style>{`
          .land-faq2-tabs { display: flex; align-items: center; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 36px; }
          .land-faq2-tab {
            background: white; border: 1.5px solid #E2E8F0; color: #475569; font-weight: 700; font-size: 13px;
            padding: 8px 18px; border-radius: 999px; cursor: pointer; font-family: inherit; transition: all 0.2s ease;
          }
          .land-faq2-tab:hover { border-color: #CBD5E1; color: #1E293B; }
          .land-faq2-tab.active { background: #0F172A; border-color: #0F172A; color: white; }

          .land-faq2-list { display: flex; flex-direction: column; gap: 12px; }
          .land-faq2-item {
            background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden;
            transition: border-color 0.25s ease, box-shadow 0.25s ease;
          }
          .land-faq2-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
          .land-faq2-trigger {
            width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px;
            padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit;
          }
          .land-faq2-q { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
          .land-faq2-icon {
            position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB;
            border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease;
          }
          .land-faq2-item.open .land-faq2-icon { background: #FFF1F2; border-color: #FECDD3; }
          .land-faq2-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
          .land-faq2-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
          .land-faq2-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
          .land-faq2-item.open .land-faq2-icon .bar { background: #EF1D28; }
          .land-faq2-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

          @media (max-width: 640px) {
            .land-faq2-q { font-size: 14.5px; }
            .land-faq2-trigger { padding: 16px 18px; }
            .land-faq2-answer { padding: 0 18px 18px; }
          }
        `}</style>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 42, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px', lineHeight: 1.15 }}>
              Frequently Asked Questions
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 16, lineHeight: 1.7, maxWidth: 480 }}>
              Everything you need to know about MarsResume.
            </p>
          </div>

          <div className="land-faq2-tabs" role="tablist" aria-label="FAQ categories">
            <button type="button" role="tab" aria-selected={faqCategory === 'all'} className={`land-faq2-tab${faqCategory === 'all' ? ' active' : ''}`}
              onClick={() => { setFaqCategory('all'); setFaqShowAll(false); }}>
              All
            </button>
            {FAQ_CATEGORIES.map(cat => (
              <button key={cat.id} type="button" role="tab" aria-selected={faqCategory === cat.id} className={`land-faq2-tab${faqCategory === cat.id ? ' active' : ''}`}
                onClick={() => { setFaqCategory(cat.id); setFaqShowAll(false); }}>
                {cat.label}
              </button>
            ))}
          </div>

          <div className="land-faq2-list">
            <AnimatePresence initial={false}>
              {visibleFaqs.map(({ q, a }) => {
                const isOpen = openFaq === q;
                const panelId = `faq-panel-${q.replace(/\W+/g, '-')}`;
                return (
                  <div key={q} className={`land-faq2-item${isOpen ? ' open' : ''}`}>
                    <button type="button" className="land-faq2-trigger" aria-expanded={isOpen} aria-controls={panelId}
                      onClick={() => setOpenFaq(isOpen ? null : q)}>
                      <span className="land-faq2-q">{q}</span>
                      <span className="land-faq2-icon">
                        <span className="bar h" />
                        <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div id={panelId} role="region" key="content"
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                          <div className="land-faq2-answer">{a}</div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </AnimatePresence>
          </div>

          {hasMoreFaqs && (
            <div style={{ textAlign: 'center', marginTop: 28 }}>
              <button type="button" onClick={() => setFaqShowAll(true)} className="land-btn-secondary">
                View All FAQs <ChevronDown size={15} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: '0 48px 0', background: '#FAFBFF', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Main CTA card */}
          <div className="land-cta-card" style={{ background: 'linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%)', borderRadius: 28, overflow: 'hidden', position: 'relative', padding: '80px 64px', border: '1px solid #1A2A3E' }}>
            {/* Background mesh */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -120, right: -80, width: 560, height: 480, background: 'radial-gradient(circle, rgba(239,29,40,0.15) 0%, transparent 60%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -80, left: -60, width: 360, height: 360, background: 'radial-gradient(circle, rgba(29,78,216,0.10) 0%, transparent 65%)', pointerEvents: 'none' }} />
            {/* Top border glow */}
            <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(239,29,40,0.5), transparent)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 64, flexWrap: 'wrap' }}>
              {/* Left: copy */}
              <div className="land-cta-copy" style={{ flex: '0 0 52%', minWidth: 300 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.25)', color: '#60A5FA', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, marginBottom: 24, letterSpacing: 0.3 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563EB', animation: 'pulse 2s infinite' }} />
                  500+ resumes built and counting
                </div>
                <h2 className="land-cta-h2" style={{ margin: '0 0 18px', fontSize: 44, fontWeight: 900, color: 'white', letterSpacing: '-1.5px', lineHeight: 1.08 }}>
                  Stop guessing why<br />
                  you're not hired.
                </h2>
                <p style={{ margin: '0 0 36px', color: '#6B7280', fontSize: 16, lineHeight: 1.75, maxWidth: 420 }}>
                  Get an AI-written resume, an honest ATS score, and the exact fixes — all in under 30 seconds.
                </p>
                {user ? (
                  <button onClick={() => navigate('/dashboard')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12 }}>
                    Go to Dashboard <ArrowRight size={17} />
                  </button>
                ) : (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                      <button onClick={() => navigate('/build/resume')} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12 }}>
                        <Sparkles size={17} /> Start Free
                      </button>

                      {uploadState === 'idle' || uploadState === 'error' ? (
                        <button onClick={handleUploadClick}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.06)', border: '1.5px dashed rgba(255,255,255,0.25)', borderRadius: 12, padding: '13px 24px', color: '#9CA3AF', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#F87171'; e.currentTarget.style.background = 'rgba(239,29,40,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#9CA3AF'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                          Upload Resume
                        </button>
                      ) : uploadState === 'parsing' ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(56,189,248,0.1)', border: '1.5px solid rgba(56,189,248,0.3)', borderRadius: 12, padding: '13px 24px', fontSize: 15, color: '#7DD3FC', fontWeight: 600 }}>
                          <div style={{ width: 16, height: 16, border: '2.5px solid rgba(125,211,252,0.3)', borderTopColor: '#7DD3FC', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                          Reading file…
                        </div>
                      ) : uploadState === 'done' ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,0.1)', border: '1.5px solid rgba(134,239,172,0.3)', borderRadius: 12, padding: '13px 24px', fontSize: 15, color: '#86EFAC', fontWeight: 600 }}>
                          ✓ Ready! Redirecting…
                        </div>
                      ) : null}
                    </div>
                    {uploadError && (
                      <div style={{ fontSize: 13, color: '#FCA5A5', background: 'rgba(239,29,40,0.1)', border: '1px solid rgba(239,29,40,0.25)', borderRadius: 8, padding: '8px 12px', maxWidth: 420 }}>
                        {uploadError}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ marginTop: 24, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {['Free forever', 'No credit card', 'Your data stays yours'].map(t => (
                    <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4B5563', fontWeight: 500 }}>
                      <Check size={11} color="#4B5563" strokeWidth={3} /> {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right: floating dashboard illustration */}
              <div className="land-cta-dash" style={{ flex: 1, minWidth: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
                <div style={{ position: 'relative', width: 340 }}>
                  {/* Soft glow */}
                  <div style={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(239,29,40,0.16) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

                  {/* Dashboard card — ATS score report */}
                  <div style={{ position: 'relative', zIndex: 2, background: 'white', borderRadius: 18, boxShadow: '0 40px 90px rgba(0,0,0,0.5)', overflow: 'hidden', animation: 'heroFloat 6s ease-in-out infinite' }}>
                    <div style={{ background: '#F8FAFC', borderBottom: '1px solid #E5E7EB', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#EF1D28,#FF6B6B)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <BarChart2 size={14} color="white" />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>Resume Score Report</div>
                        <div style={{ fontSize: 10, color: '#9CA3AF' }}>Scanned 30 seconds ago</div>
                      </div>
                      <div style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 900, color: '#059669', flexShrink: 0 }}>94%</div>
                    </div>
                    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[
                        { title: 'Keywords Matched', color: '#059669', Icon: Check, value: '18/20', valueColor: '#059669', valueBg: '#ECFDF5' },
                        { title: 'ATS-Safe Formatting', color: '#059669', Icon: Check, value: 'Passed', valueColor: '#059669', valueBg: '#ECFDF5' },
                        { title: 'Suggested Fixes', color: '#D97706', Icon: Target, value: '2 found', valueColor: '#B45309', valueBg: '#FFFBEB' },
                      ].map(row => (
                        <div key={row.title} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #F1F5F9', borderRadius: 12, background: '#FAFBFF' }}>
                          <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: row.color, flexShrink: 0 }} />
                          <row.Icon size={14} color={row.color} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#111827', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</div>
                          <span style={{ fontSize: 10, fontWeight: 800, color: row.valueColor, background: row.valueBg, borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Floating badge — 30-Second Scan */}
                  <div style={{ position: 'absolute', top: 10, right: -30, zIndex: 3, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: 12, padding: '9px 14px', boxShadow: '0 14px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(253,230,138,0.8)', display: 'flex', alignItems: 'center', gap: 8, animation: 'heroFloat 5s ease-in-out infinite 0.3s' }}>
                    <Zap size={14} color="#D97706" />
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#92400E' }}>30-Second Scan</span>
                  </div>

                  {/* Floating badge — Exact Fixes Included */}
                  <div style={{ position: 'absolute', bottom: -18, left: -22, zIndex: 3, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: 12, padding: '9px 14px', boxShadow: '0 14px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(167,243,208,0.8)', display: 'flex', alignItems: 'center', gap: 8, animation: 'heroFloat 6.5s ease-in-out infinite 0.8s' }}>
                    <CheckCircle2 size={14} color="#059669" />
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#065F46' }}>Exact Fixes Included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: '#FAFAFA', borderTop: '1px solid #F3F4F6', padding: '72px 48px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>

          {/* Top footer: brand + nav */}
          <div className="land-footer-grid" style={{ display: 'flex', justifyContent: 'space-between', gap: 48, paddingBottom: 56, borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ flex: '0 0 auto', maxWidth: 280 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 34, height: 34, objectFit: 'contain' }} />
                <div>
                  <div style={{ color: '#EF1D28', fontWeight: 900, fontSize: 17, lineHeight: 1 }}>MarsResume</div>
                  <div style={{ color: '#9CA3AF', fontSize: 9, marginTop: 3, fontWeight: 400 }}>Resume That Takes You to Mars</div>
                </div>
              </div>
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280', lineHeight: 1.8 }}>
                AI-powered resume builder helping professionals worldwide land better jobs, faster.
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {SOCIAL_LINKS.map(({ Icon, label, href }) => (
                  <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: 10, background: 'white', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#E5E7EB'; }}>
                    <Icon size={14} color="#6B7280" />
                  </a>
                ))}
              </div>
              {/* Mini trust badges */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { icon: ShieldCheck, text: 'SSL encrypted & secure' },
                  { icon: Users, text: '100+ professionals' },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#9CA3AF' }}>
                    <Icon size={13} color="#D1D5DB" /> {text}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
              {[
                { heading: 'Product', links: [['Resume Builder', () => goTo('/signup')], ['CV Builder', () => goTo('/signup')], ['Cover Letter', () => goTo('/signup')], ['ATS Analyzer', () => navigate('/ats-checker')], ['Pricing', () => navigate('/pricing')]] },
                { heading: 'Resources', links: [['How It Works', () => navigate('/how-it-works')], ['ATS Guide', () => navigate('/ats-guide')], ['Resume Tips', () => navigate('/resume-tips')], ['Interview Prep', () => goTo('/signup')], ['Career Blog', () => navigate('/blog')]] },
                { heading: 'Company', links: [['About Us', () => navigate('/about')], ['Contact', () => navigate('/contact')], ['Privacy Policy', () => navigate('/privacy-policy')], ['Terms of Service', () => navigate('/terms-of-use')], ['Cookie Policy', () => navigate('/cookie-policy')]] },
              ].map(({ heading, links }) => (
                <div key={heading}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 20 }}>{heading}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                    {links.map(([label, onClick]) => (
                      <button key={label} onClick={onClick} style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 13, cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: 'inherit', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#111827'}
                        onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ padding: '24px 0 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ color: '#9CA3AF', fontSize: 12 }}>
              © {new Date().getFullYear()} Devops Markaz Tech Pvt. Ltd. · All rights reserved
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {[['Privacy', '/privacy-policy'], ['Terms', '/terms-of-use'], ['Contact', '/contact']].map(([label, path]) => (
                <button key={label} onClick={() => navigate(path)} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#374151'}
                  onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ── Pricing Page ──────────────────────────────────────────────────────────────
export const MarketingFooter = ({ navigate, goTo }) => (
  <footer style={{ width: '100vw', boxSizing: 'border-box', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', marginTop: 72, background: '#FAFAFA', borderTop: '1px solid #F3F4F6', padding: '72px 48px 0' }}>
    <div style={{ maxWidth: 1140, margin: '0 auto' }}>
      <div className="land-footer-grid" style={{ display: 'flex', justifyContent: 'space-between', gap: 48, paddingBottom: 56, borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ flex: '0 0 auto', maxWidth: 280 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 34, height: 34, objectFit: 'contain' }} />
            <div>
              <div style={{ color: '#EF1D28', fontWeight: 900, fontSize: 17, lineHeight: 1 }}>MarsResume</div>
              <div style={{ color: '#9CA3AF', fontSize: 9, marginTop: 3, fontWeight: 400 }}>Resume That Takes You to Mars</div>
            </div>
          </div>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280', lineHeight: 1.8 }}>
            AI-powered resume builder helping professionals worldwide land better jobs, faster.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {SOCIAL_LINKS.map(({ Icon, label, href }) => (
              <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: 10, background: 'white', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#E5E7EB'; }}>
                <Icon size={14} color="#6B7280" />
              </a>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {[
              { icon: ShieldCheck, text: 'SSL encrypted & secure' },
              { icon: Users, text: '100+ professionals' },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#9CA3AF' }}>
                <Icon size={13} color="#D1D5DB" /> {text}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
          {[
            { heading: 'Product', links: [['Resume Builder', () => goTo('/signup')], ['CV Builder', () => goTo('/signup')], ['Cover Letter', () => goTo('/signup')], ['ATS Analyzer', () => navigate('/ats-checker')], ['Pricing', () => navigate('/pricing')]] },
            { heading: 'Resources', links: [['How It Works', () => navigate('/how-it-works')], ['ATS Guide', () => navigate('/ats-guide')], ['Resume Tips', () => navigate('/resume-tips')], ['Interview Prep', () => goTo('/signup')], ['Career Blog', () => navigate('/blog')]] },
            { heading: 'Company', links: [['About Us', () => navigate('/about')], ['Contact', () => navigate('/contact')], ['Privacy Policy', () => navigate('/privacy-policy')], ['Terms of Service', () => navigate('/terms-of-use')], ['Cookie Policy', () => navigate('/cookie-policy')]] },
          ].map(({ heading, links }) => (
            <div key={heading}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 20 }}>{heading}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                {links.map(([label, onClick]) => (
                  <button key={label} onClick={onClick} style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 13, cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: 'inherit', transition: 'color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#111827'}
                    onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '24px 0 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ color: '#9CA3AF', fontSize: 12 }}>
          &copy; {new Date().getFullYear()} Devops Markaz Tech Pvt. Ltd. &middot; All rights reserved
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {[['Privacy', '/privacy-policy'], ['Terms', '/terms-of-use'], ['Contact', '/contact']].map(([label, path]) => (
            <button key={label} onClick={() => navigate(path)} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0, transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#374151'}
              onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

const FEATURE_EXAMPLES = ['Software Engineering', 'Engineering', 'Back-End Developer', 'Student Internship', 'Business', 'Healthcare'];
const FEATURE_COLUMNS = [
  {
    heading: 'Build',
    items: [
      { Icon: FileText, bg: '#FFF1F2', ic: '#EF1D28', title: 'Resume Builder', desc: 'Create your best resume. Get hired.', path: '/signup' },
      { Icon: Mail, bg: '#F0F9FF', ic: '#0891B2', title: 'Cover Letter Builder', desc: 'Attach a matching cover letter.', path: '/signup' },
      { Icon: Sparkles, bg: '#F5F3FF', ic: '#7C3AED', title: 'AI Resume Writer', desc: 'Let AI write your resume.', path: '/signup' },
      { Icon: Zap, bg: '#FFFBEB', ic: '#D97706', title: 'AI Cover Letter Writer', desc: 'AI-powered cover letter writer.', path: '/signup' },
    ],
  },
  {
    heading: 'AI Tools',
    items: [
      { Icon: Target, bg: '#ECFDF5', ic: '#059669', title: 'ATS Checker', desc: 'Get instant feedback for your resume.', path: '/ats-checker' },
      { Icon: BarChart2, bg: '#EFF6FF', ic: '#2563EB', title: 'AI Job Tailoring', desc: 'Tailor your resume to any job.', path: '/signup' },
      { Icon: Video, bg: '#FFF1F2', ic: '#EF1D28', title: 'AI Video Interview', desc: 'Practice on camera with real AI feedback.', path: '/ai-video-interview' },
      { Icon: Users, bg: '#FDF2F8', ic: '#DB2777', title: 'Interview Prep', desc: 'Ace your next interview with AI.', path: '/signup' },
      { Icon: Linkedin, bg: '#E8F0FE', ic: '#0A66C2', title: 'LinkedIn Analyzer', desc: 'Score and improve your LinkedIn profile.', path: '/signup' },
      { Icon: Download, bg: '#F0FDF4', ic: '#16A34A', title: 'PDF Export', desc: 'Export in one click, ready to send.', path: '/signup' },
    ],
  },
];
const RESUME_MEGA_SECTIONS = [
  {
    heading: 'Build',
    items: [
      { Icon: FileText, bg: '#FFF1F2', ic: '#EF1D28', title: 'Resume Builder', desc: 'Create your best resume and start applying faster.', path: '/signup' },
      { Icon: Target, bg: '#ECFDF5', ic: '#059669', title: 'ATS Resume Checker', desc: 'Get instant feedback to improve resume performance.', path: '/ats-checker' },
    ],
  },
  {
    heading: 'Templates',
    items: [
      { Icon: Layers, bg: '#EFF6FF', ic: '#2563EB', title: 'Resume Templates', desc: 'Browse resume layouts for different careers and styles.', path: '/templates#resumes' },
      { Icon: Star, bg: '#F5F3FF', ic: '#7C3AED', title: 'Resume Examples', desc: 'See complete examples before choosing your template.', path: '/templates#resumes' },
    ],
  },
  {
    heading: 'Popular',
    links: [
      { label: 'Software Engineering', path: '/templates#resumes' },
      { label: 'Back-End Developer', path: '/templates#resumes' },
      { label: 'Business', path: '/templates#resumes' },
      { label: 'Student Internship', path: '/templates#resumes' },
    ],
  },
];
const COVER_LETTER_MEGA_SECTIONS = [
  {
    heading: 'Create',
    items: [
      { Icon: Mail, bg: '#F0F9FF', ic: '#0891B2', title: 'Cover Letter Builder', desc: 'Write a tailored cover letter with the same polished flow.', path: '/signup' },
    ],
  },
  {
    heading: 'Templates',
    items: [
      { Icon: Layers, bg: '#ECFDF5', ic: '#059669', title: 'Cover Letter Templates', desc: 'Choose matching letter layouts for every application.', path: '/templates#cover-letters' },
    ],
  },
  {
    heading: 'AI Help',
    items: [
      { Icon: Zap, bg: '#FFFBEB', ic: '#D97706', title: 'AI Cover Letter Writer', desc: 'Generate stronger letters faster with AI assistance.', path: '/signup' },
    ],
  },
];

const FeaturesMegaMenu = ({ featureColumns = FEATURE_COLUMNS, handleNav }) => (
  <div style={{
    position: 'fixed',
    top: 64,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100vw - 8px)',
    maxWidth: 'none',
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '0 0 24px 24px',
    boxShadow: '0 28px 80px rgba(15,23,42,0.12)',
    overflow: 'hidden',
    zIndex: 300,
  }}>
    <div style={{ padding: '34px 36px 36px' }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', marginBottom: 24 }}>Explore MarsResume</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20 }}>
          {featureColumns.map((column, columnIndex) => (
            <div key={column.heading} style={{ paddingRight: columnIndex < featureColumns.length - 1 ? 8 : 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
                {column.heading}
              </div>
              {column.items.map(({ Icon, bg, ic, title, desc, path }) => (
                <button
                  key={title}
                  onClick={() => handleNav(path)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    width: '100%',
                    padding: '10px 4px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: 12,
                    marginBottom: 4,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} color={ic} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#111827', marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.45 }}>{desc}</div>
                  </div>
                </button>
              ))}
            </div>
          ))}
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
              Resume Examples
            </div>
            {FEATURE_EXAMPLES.map(ex => (
              <button
                key={ex}
                onClick={() => handleNav('/templates#resumes')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '13px 0',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: '#374151',
                  borderBottom: '1px solid #F3F4F6',
                  textAlign: 'left',
                  fontWeight: 500,
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#EF1D28'}
                onMouseLeave={e => e.currentTarget.style.color = '#374151'}
              >
                {ex} <ArrowRight size={13} color="#9CA3AF" />
              </button>
            ))}
            <button onClick={() => handleNav('/templates')} style={{ marginTop: 14, fontSize: 13, color: '#EF1D28', fontWeight: 800, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              View all examples ->
            </button>
          </div>
        </div>
    </div>
  </div>
);

const HeaderMegaMenu = ({ title, sections, handleNav }) => (
  <div style={{
    position: 'fixed',
    top: 64,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100vw - 8px)',
    maxWidth: 'none',
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '0 0 24px 24px',
    boxShadow: '0 28px 80px rgba(15,23,42,0.12)',
    overflow: 'hidden',
    zIndex: 300,
  }}>
    <div style={{ padding: '34px 36px 36px' }}>
      <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', marginBottom: 24 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))`, gap: 20 }}>
        {sections.map((section, index) => (
          <div key={section.heading} style={{ paddingRight: index < sections.length - 1 ? 8 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
              {section.heading}
            </div>
            {section.items?.map(({ Icon, bg, ic, title: itemTitle, desc, path }) => (
              <button
                key={itemTitle}
                onClick={() => handleNav(path)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  width: '100%',
                  padding: '10px 4px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderRadius: 12,
                  marginBottom: 4,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 38, height: 38, borderRadius: 11, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={16} color={ic} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#111827', marginBottom: 3 }}>{itemTitle}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.45 }}>{desc}</div>
                </div>
              </button>
            ))}
            {section.links?.map(({ label, path }) => (
              <button
                key={label}
                onClick={() => handleNav(path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '13px 0',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: '#374151',
                  borderBottom: '1px solid #F3F4F6',
                  textAlign: 'left',
                  fontWeight: 500,
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#EF1D28'}
                onMouseLeave={e => e.currentTarget.style.color = '#374151'}
              >
                {label} <ArrowRight size={13} color="#9CA3AF" />
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const MarketingHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimer = React.useRef(null);
  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  const openMenu = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(name);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const handleNav = (path) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (path === '/signup') goTo(path);
    else navigate(path);
    setOpenDropdown(null);
  };

  return (
    <>
      <LandingStyles />
      <div style={{ background: '#0D1117', padding: '9px 24px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <span style={{ background: '#EF1D28', color: 'white', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>NEW</span>
        <span style={{ fontSize: 13, color: '#8B949E' }}>
          <span style={{ color: '#E6EDF3', fontWeight: 600 }}>ATS Analyzer</span> & <span style={{ color: '#E6EDF3', fontWeight: 600 }}>AI Tailoring</span> now live -
        </span>
        <button onClick={() => goTo('/signup')} style={{ background: 'none', border: 'none', color: '#EF1D28', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Try free <ArrowRight size={12} />
        </button>
      </div>

      <nav className="marketing-header-nav" style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #F3F4F6', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
          <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 34, height: 34, objectFit: 'contain' }} />
          <div className="marketing-header-logo-text" style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#EF1D28', lineHeight: 1.1 }}>MarsResume</div>
            <div style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 400, lineHeight: 1 }}>Resume That Takes You to Mars</div>
          </div>
        </button>

        <div className="marketing-header-links" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ position: 'relative' }} onMouseEnter={() => openMenu('features')} onMouseLeave={scheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'features' ? '#EF1D28' : undefined }}>
              Features <ChevronDown size={13} />
            </button>
            {openDropdown === 'features' && <FeaturesMegaMenu handleNav={handleNav} />}
          </div>

          <div style={{ position: 'relative' }} onMouseEnter={() => openMenu('resume')} onMouseLeave={scheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'resume' ? '#EF1D28' : undefined }}>
              Resume <ChevronDown size={13} />
            </button>
            {openDropdown === 'resume' && <HeaderMegaMenu title="Explore Resumes" sections={RESUME_MEGA_SECTIONS} handleNav={handleNav} />}
          </div>

          <div style={{ position: 'relative' }} onMouseEnter={() => openMenu('coverletter')} onMouseLeave={scheduleClose}>
            <button className="land-nav-link" style={{ display: 'flex', alignItems: 'center', gap: 4, color: openDropdown === 'coverletter' ? '#EF1D28' : undefined }}>
              Cover Letter <ChevronDown size={13} />
            </button>
            {openDropdown === 'coverletter' && <HeaderMegaMenu title="Explore Cover Letters" sections={COVER_LETTER_MEGA_SECTIONS} handleNav={handleNav} />}
          </div>

          <button className="land-nav-link" onClick={() => navigate('/ats-checker')}>ATS Checker</button>
          <button onClick={() => navigate('/ai-video-interview')} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#EF1D28,#FF5C3A)', color: 'white', border: 'none', borderRadius: 999, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 10px rgba(239,29,40,0.35)', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(239,29,40,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(239,29,40,0.35)'; }}>
            <Video size={14} strokeWidth={2.5} />
            AI Interview
          </button>
          <button className="land-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
        </div>

        <div className="marketing-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {user ? (
            <>
              <span style={{ color: '#9CA3AF', fontSize: 13, padding: '0 8px', fontWeight: 500 }}>Hi, {user.name?.split(' ')[0]}</span>
              <button onClick={() => navigate('/dashboard')} className="land-btn-primary" style={{ padding: '9px 20px', fontSize: 13, borderRadius: 9 }}>Dashboard <ArrowRight size={14} /></button>
            </>
          ) : (
            <>
              <button className="marketing-header-login" onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 700, borderRadius: 9, border: '1.5px solid #EF1D28', background: 'white', color: '#EF1D28', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#FFF1F2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                Log In
              </button>
              <button onClick={() => navigate('/signup')} className="land-btn-primary" style={{ padding: '9px 20px', fontSize: 13, borderRadius: 9 }}>Create My Resume</button>
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export const AtsCheckerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [uploadState, setUploadState] = useState('idle');
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = React.useRef(null);

  usePageSEO({
    title: 'ATS Checker - MarsResume | Check Your Resume ATS Score',
    description: 'Upload your resume and check how it performs with ATS systems. Get fast feedback on structure, keywords, and readability before you apply.',
    canonical: '/ats-checker',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const goToBuilder = () => navigate(user ? '/dashboard' : '/signup');
  const atsPreview = {
    ...buildShowcaseResume({ id: 'harvard_standard' }),
    personalInfo: {
      ...buildShowcaseResume({ id: 'harvard_standard' }).personalInfo,
      fullName: 'Maya Patel',
      jobTitle: 'Operations Lead',
    },
  };

  const handleUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.type !== 'application/pdf') { setUploadError('Only PDF files are supported.'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('File must be under 5 MB.'); return; }
    setUploadState('parsing');
    setUploadError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      localStorage.setItem('mars_pending_file', base64);
      localStorage.setItem('mars_pending_filename', file.name);
      setUploadState('done');
      setTimeout(() => navigate(user ? '/dashboard' : '/signup?from=upload'), 500);
    } catch {
      setUploadState('error');
      setUploadError('Failed to read file. Please try again.');
    }
  };

  React.useEffect(() => {
    if (user && localStorage.getItem('mars_pending_file')) navigate('/dashboard');
  }, [user, navigate]);

  const scoreCards = [
    { title: 'Keywords', value: '91%', desc: 'Finds missing role terms and skill matches recruiters expect.', color: '#059669', bg: '#ECFDF5' },
    { title: 'Format', value: '94%', desc: 'Checks section order, readability, and ATS-safe layout structure.', color: '#2563EB', bg: '#EFF6FF' },
    { title: 'Clarity', value: '88%', desc: 'Flags weak bullets, vague summaries, and low-impact wording.', color: '#7C3AED', bg: '#F5F3FF' },
  ];

  const atsSteps = [
    { n: '1', title: 'Upload your resume', desc: 'Use a PDF version of your current resume to start the scan.' },
    { n: '2', title: 'Review your ATS score', desc: 'See a quick breakdown across keywords, structure, and readability.' },
    { n: '3', title: 'Fix the weak spots', desc: 'Apply the suggestions, then move into the builder to improve your draft.' },
  ];

  const atsChecks = [
    { icon: Target, title: 'Keyword alignment', desc: 'Spot missing role-specific phrases, hard skills, and action words that job descriptions often repeat.' },
    { icon: Layers, title: 'Section structure', desc: 'Make sure your summary, experience, skills, and education are organized in a recruiter-friendly order.' },
    { icon: FileText, title: 'Content quality', desc: 'Catch generic bullets, thin accomplishments, and unclear phrasing before you send applications out.' },
    { icon: ShieldCheck, title: 'ATS-safe formatting', desc: 'Avoid layout choices that can confuse parsing systems, including overly decorative structure and weak hierarchy.' },
  ];

  const atsFaqs = [
    ['What file format should I upload?', 'PDF works best — it preserves formatting and is the standard for ATS submission. Keep the file under 5 MB. If you built your resume on MarsResume you can scan it directly from your dashboard without uploading anything.'],
    ['How is the ATS score calculated?', 'The scan checks 40+ criteria including keyword presence, section headers, formatting compatibility, file readability, length, and more. Each criterion is weighted by how often real ATS systems flag it. The final score is out of 100 — anything above 80 is considered ATS-ready.'],
    ['How do I use the job description tailoring?', 'Paste the full job description into the "Tailor for Job" field after uploading your resume. The tool highlights keywords from the job description that are missing from your resume, so you know exactly what to add or emphasise before applying.'],
    ['Will this rewrite my resume automatically?', 'No — the checker gives you a detailed score and specific improvement recommendations. You then apply changes yourself in the builder (or edit manually) so the final wording stays in your voice. You stay in full control of the content.'],
    ['Can I check my resume without an account?', 'Yes. You can upload and scan without logging in. Creating a free account lets you save your results, track score improvements over time, and open the resume directly in the builder to fix issues immediately.'],
    ['What do I do after getting my score?', 'Review each flagged issue — the tool tells you exactly what to fix and why. Click "Open in Builder" to jump straight to your resume and make changes. Re-scan after editing to confirm your score has improved. Aim for 80+ before submitting applications.'],
  ];

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#FFFFFF', overflowX: 'hidden' }}>
      <LandingStyles />
      <MarketingHeader />

      <section style={{ background: 'linear-gradient(180deg,#FAFAFA 0%,#FFFFFF 100%)', padding: '80px 48px 72px', position: 'relative', overflow: 'hidden', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#E5E7EB 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.5, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 800, height: 400, background: 'radial-gradient(ellipse, rgba(239,29,40,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="land-hero-grid" style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 60, position: 'relative' }}>
          <div style={{ flex: '0 0 52%', animation: 'fadeUp 0.6s ease both' }}>
            <h1 style={{ margin: '0 0 22px', fontSize: 60, fontWeight: 900, color: '#0D1117', lineHeight: 1.05, letterSpacing: '-2.5px' }}>
              Check Your Resume
              <br />
              <span style={{ color: '#EF1D28' }}>ATS Score Before You Apply</span>
            </h1>

            <p style={{ margin: '0 0 36px', fontSize: 18, color: '#6B7280', lineHeight: 1.7, maxWidth: 500 }}>
              Upload your resume to see how it performs with applicant tracking systems. Spot missing keywords, weak formatting, and clarity issues before recruiters do.
            </p>

            <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                <button onClick={goToBuilder} className="land-btn-primary" style={{ fontSize: 16, padding: '14px 30px', borderRadius: 12 }}>
                  <ArrowRight size={17} /> Create Free Resume
                </button>

                {uploadState === 'idle' || uploadState === 'error' ? (
                  <button onClick={handleUploadClick}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: '1.5px dashed #D1D5DB', borderRadius: 12, padding: '13px 20px', color: '#6B7280', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#EF1D28'; e.currentTarget.style.background = '#FFF1F2'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'none'; }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Check ATS With My Resume
                  </button>
                ) : uploadState === 'parsing' ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#FFF1F2', border: '1.5px solid #FECACA', borderRadius: 12, padding: '13px 20px', fontSize: 15, color: '#B91C1C', fontWeight: 600 }}>
                    <div style={{ width: 16, height: 16, border: '2.5px solid #FECACA', borderTopColor: '#EF1D28', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                    Scanning resume…
                  </div>
                ) : (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFF1F2', border: '1.5px solid #FECACA', borderRadius: 12, padding: '13px 20px', fontSize: 15, color: '#B91C1C', fontWeight: 600 }}>
                    Ready! Redirecting…
                  </div>
                )}
              </div>

              {uploadError && (
                <div style={{ fontSize: 13, color: '#DC2626', background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', maxWidth: 420 }}>
                  {uploadError}
                </div>
              )}
              {(uploadState === 'idle' || uploadState === 'error') && (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>PDF only · max 5 MB · upload to get an ATS score preview</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['Score keywords', 'Check structure', 'Highlight weak sections'].map(t => (
                <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', fontWeight: 500 }}>
                  <Check size={12} color="#059669" strokeWidth={3} /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="land-hero-visual" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 580 }}>
            <div style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(239,29,40,0.10) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

            <div style={{ width: 460, background: 'white', borderRadius: 18, boxShadow: '0 32px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0', overflow: 'hidden', animation: 'heroFloat 5s ease-in-out infinite', position: 'relative', zIndex: 2 }}>
              <div style={{ background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#FC5F5A' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#FDBA2F' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
                </div>
                <div style={{ flex: 1, background: 'white', borderRadius: 7, padding: '5px 10px', marginLeft: 6, fontSize: 10, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 5, border: '1px solid #E2E8F0' }}>
                  <span style={{ fontSize: 9 }}>🔒</span>
                  <span style={{ fontWeight: 500 }}>marsresume.com/ats-checker</span>
                </div>
              </div>

              <div style={{ height: 500, overflow: 'hidden', position: 'relative' }}>
                <div style={{ transform: 'scale(0.638)', transformOrigin: 'top left', width: 721, pointerEvents: 'none' }}>
                  <ResumePreview data={atsPreview} />
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, transparent, white)', pointerEvents: 'none' }} />
              </div>
            </div>

            <div style={{ position: 'absolute', top: 30, right: -14, background: 'white', borderRadius: 14, padding: '11px 15px', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5s ease-in-out infinite 0.4s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#FFF1F2,#FECACA)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircle2 size={17} color="#EF1D28" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#B91C1C', lineHeight: 1.2 }}>ATS Match: 92%</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Strong keyword coverage</div>
              </div>
            </div>

            <div style={{ position: 'absolute', top: '42%', left: -18, background: 'white', borderRadius: 14, padding: '11px 15px', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5s ease-in-out infinite 0.8s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#DBEAFE,#BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BarChart2 size={17} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>Formatting Check</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Readable and clean</div>
              </div>
            </div>

            <div style={{ position: 'absolute', bottom: 44, left: -10, background: 'white', borderRadius: 14, padding: '11px 15px', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 10, animation: 'heroFloat 5s ease-in-out infinite 1.2s', zIndex: 3 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#FFF1F2,#FECACA)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Sparkles size={17} color="#EF1D28" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#B91C1C', lineHeight: 1.2 }}>Actionable Tips</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Section-by-section guidance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: '#FFFFFF', padding: '72px 24px 24px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 34 }}>
            <h2 style={{ margin: '0 0 10px', fontSize: 40, fontWeight: 900, letterSpacing: -1.2, color: '#0F172A' }}>A quick ATS read before you apply</h2>
            <p style={{ margin: '0 auto', maxWidth: 720, fontSize: 16, color: '#64748B', lineHeight: 1.75 }}>
              The checker helps you see whether your resume is easy to parse, aligned with job language, and strong enough to move into interviews.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 18, marginBottom: 52 }}>
            {scoreCards.map(card => (
              <div key={card.title} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 20, padding: '26px 24px', boxShadow: '0 10px 32px rgba(15,23,42,0.05)' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: card.bg, color: card.color, borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800, marginBottom: 18 }}>
                  {card.title}
                </div>
                <div style={{ fontSize: 38, fontWeight: 900, color: '#0F172A', letterSpacing: -1 }}>{card.value}</div>
                <div style={{ marginTop: 10, color: '#64748B', fontSize: 14, lineHeight: 1.7 }}>{card.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 24, marginBottom: 56 }}>
            <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 24, padding: '28px 28px 30px' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#EF1D28', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 10 }}>How it works</div>
              <h3 style={{ margin: '0 0 18px', fontSize: 30, fontWeight: 900, color: '#0F172A', letterSpacing: -0.8 }}>Scan first, then improve with confidence</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                {atsSteps.map(step => (
                  <div key={step.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, background: 'white', border: '1px solid #E5E7EB', borderRadius: 16, padding: 16 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 12, background: '#FFF1F2', color: '#EF1D28', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, flexShrink: 0 }}>{step.n}</div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{step.title}</div>
                      <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: 'linear-gradient(180deg,#0F172A 0%,#172554 100%)', borderRadius: 24, padding: '30px 28px', color: 'white' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#FCA5A5', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 10 }}>Why it helps</div>
              <h3 style={{ margin: '0 0 14px', fontSize: 28, fontWeight: 900, letterSpacing: -0.7 }}>Catch problems before recruiters do</h3>
              <p style={{ margin: '0 0 22px', color: '#CBD5E1', fontSize: 14, lineHeight: 1.8 }}>
                A resume can look polished and still miss obvious ATS signals. This page gives you a faster way to validate the basics before sending applications out.
              </p>
              <div style={{ display: 'grid', gap: 12 }}>
                {['See if your keywords reflect the role you want', 'Reduce formatting issues that block parsing', 'Make thin bullet points easier to strengthen', 'Move into the builder with a clear next step'].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: '#E2E8F0', fontSize: 14, lineHeight: 1.65 }}>
                    <CheckCircle2 size={16} color="#FCA5A5" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleUploadClick} style={{ marginTop: 24, background: '#EF1D28', color: 'white', border: 'none', borderRadius: 12, padding: '13px 20px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Upload Resume For ATS <ArrowRight size={15} />
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 56 }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 34, fontWeight: 900, color: '#0F172A', letterSpacing: -0.9 }}>What we review in your resume</h3>
              <p style={{ margin: '0 auto', maxWidth: 700, color: '#64748B', fontSize: 15, lineHeight: 1.75 }}>
                The score is meant to be practical. It focuses on the areas that most often decide whether a resume gets read clearly by systems and humans.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 18 }}>
              {atsChecks.map(({ icon: Icon, title, desc }) => (
                <div key={title} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 18, padding: '22px 22px 20px', boxShadow: '0 8px 24px rgba(15,23,42,0.04)' }}>
                  <div style={{ width: 46, height: 46, borderRadius: 14, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <Icon size={20} color="#EF1D28" />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 850, color: '#111827', marginBottom: 8 }}>{title}</div>
                  <div style={{ color: '#64748B', fontSize: 14, lineHeight: 1.75 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: '12px 0 0', maxWidth: 700, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <h3 style={{ margin: 0, fontSize: 38, fontWeight: 900, color: '#0D1117', letterSpacing: '-0.8px', lineHeight: 1.2 }}>
                Frequently asked questions
              </h3>
              <p style={{ margin: '18px auto 0', color: '#6B7280', fontSize: 15, maxWidth: 500, lineHeight: 1.75 }}>
                A few quick answers for what people usually want to know before running an ATS scan.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {atsFaqs.map(([q, a], i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={q}
                    className="land-faq-item"
                    style={{
                      borderRadius: 16,
                      borderColor: isOpen ? '#FECACA' : '#E5E7EB',
                      background: '#FFFFFF',
                      boxShadow: isOpen ? '0 10px 30px rgba(239,29,40,0.08)' : 'none',
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16, fontFamily: 'inherit' }}
                    >
                      <span style={{ fontSize: 15.5, fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>{q}</span>
                      <div style={{ position: 'relative', width: 30, height: 30, borderRadius: 9, background: isOpen ? '#FFF1F2' : '#F9FAFB', border: '1px solid', borderColor: isOpen ? '#FECDD3' : '#E5E7EB', flexShrink: 0, transition: 'background 0.25s ease, border-color 0.25s ease' }}>
                        <span style={{ position: 'absolute', top: '50%', left: '50%', width: 12, height: 2, margin: '-1px 0 0 -6px', borderRadius: 2, background: isOpen ? '#EF1D28' : '#6B7280' }} />
                        <motion.span
                          animate={{ rotate: isOpen ? 90 : 0 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          style={{ position: 'absolute', top: '50%', left: '50%', width: 2, height: 12, margin: '-6px 0 0 -1px', borderRadius: 2, background: isOpen ? '#EF1D28' : '#6B7280' }}
                        />
                      </div>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}
                        >
                          <div style={{ padding: '0 24px 22px', color: '#6B7280', fontSize: 14.5, lineHeight: 1.8, maxWidth: 760 }}>
                            {a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

// ── Shared policy page layout ──────────────────────────────────────────────────
const POLICY_NAV_CSS = `
  .pol-nav-link {
    background: none; border: none; color: #4B5563; font-weight: 500;
    cursor: pointer; font-size: 14px; padding: 8px 14px; border-radius: 8px;
    font-family: inherit; transition: all 0.15s;
  }
  .pol-nav-link:hover { color: #111827; background: #F3F4F6; }
  .pol-btn-primary {
    background: #EF1D28; color: white; border: none; border-radius: 10px;
    padding: 9px 20px; font-weight: 700; font-size: 13px; cursor: pointer;
    font-family: inherit; display: inline-flex; align-items: center; gap: 7px;
    transition: all 0.2s;
  }
  .pol-btn-primary:hover { background: #D91620; transform: translateY(-1px); box-shadow: 0 8px 20px rgba(239,29,40,0.3); }
  .pol-footer-grid { display: flex; justify-content: space-between; gap: 48px; }
  .pol-content-card { padding: 0; overflow: hidden; }
  .pol-card-header { padding: 32px 48px 28px; background: linear-gradient(135deg,#FAFBFF 0%,#F8FAFC 100%); border-bottom: 1px solid #E8EDF2; }
  .pol-card-body { padding: 0 48px 40px; }
  .pol-section { padding: 28px 0; border-bottom: 1px solid #F1F5F9; transition: background 0.15s; scroll-margin-top: 20px; }
  .pol-section:last-child { border-bottom: none; padding-bottom: 8px; }
  .pol-section-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
  .pol-section-num {
    width: 34px; height: 34px; border-radius: 10px;
    background: var(--pol-accent-gradient, linear-gradient(135deg,#EF1D28,#FF5C5C));
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; flex-shrink: 0; margin-top: 1px;
    box-shadow: var(--pol-accent-shadow, 0 3px 10px rgba(239,29,40,0.25));
  }
  .pol-section-title { margin: 5px 0 0; font-size: 17px; font-weight: 800; color: #0F172A; line-height: 1.3; flex: 1; }
  .pol-section-body { padding-left: 48px; }
  .pol-p { margin: 0 0 11px; font-size: 14.5px; color: #374151; line-height: 1.85; }
  .pol-ul { padding-left: 18px; margin: 8px 0 12px; }
  .pol-ul li { margin-bottom: 8px; font-size: 14px; color: #374151; line-height: 1.75; }
  .pol-highlight { background: linear-gradient(135deg,#FFFBEB,#FFF7ED); border: 1px solid #FDE68A; border-left: 4px solid #F59E0B; border-radius: 10px; padding: 14px 18px; margin: 14px 0; font-size: 13.5px; color: #78350F; line-height: 1.75; }
  .pol-info { background: linear-gradient(135deg,#EFF6FF,#F0F9FF); border: 1px solid #BFDBFE; border-left: 4px solid #3B82F6; border-radius: 10px; padding: 14px 18px; margin: 14px 0; font-size: 13.5px; color: #1E3A8A; line-height: 1.75; }
  .pol-success { background: linear-gradient(135deg,#F0FDF4,#ECFDF5); border: 1px solid #BBF7D0; border-left: 4px solid #16A34A; border-radius: 10px; padding: 14px 18px; margin: 14px 0; font-size: 13.5px; color: #14532D; line-height: 1.75; }
  .pol-summary { background: linear-gradient(135deg, var(--pol-summary-bg1,#FFF1F2), var(--pol-summary-bg2,#FFF5F5)); border: 1px solid var(--pol-summary-border,#FECACA); border-radius: 14px; padding: 20px 24px; margin-bottom: 8px; }
  .pol-summary-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: var(--pol-accent,#EF1D28); margin: 0 0 10px; }
  .pol-summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; }
  .pol-summary-item { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: #374151; line-height: 1.55; }
  .pol-summary-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--pol-accent,#EF1D28); flex-shrink: 0; margin-top: 5px; }
  .pol-toc { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 22px; margin: 24px 0 4px; }
  .pol-toc-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #9CA3AF; margin: 0 0 12px; }
  .pol-toc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 4px 16px; }
  .pol-toc-link { background: none; border: none; padding: 4px 0; font-size: 13px; color: #6B7280; cursor: pointer; font-family: inherit; text-align: left; transition: color 0.15s; display: flex; align-items: center; gap: 6px; }
  .pol-toc-link:hover { color: var(--pol-accent,#EF1D28); }
  .pol-toc-num { font-size: 11px; font-weight: 700; color: var(--pol-accent,#EF1D28); min-width: 18px; }
  .pol-table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 13px; border-radius: 10px; overflow: hidden; border: 1px solid #E2E8F0; }
  .pol-table th { background: #F8FAFC; padding: 11px 14px; text-align: left; border-bottom: 2px solid #E2E8F0; font-weight: 700; color: #0F172A; font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; }
  .pol-table td { padding: 11px 14px; border-bottom: 1px solid #F1F5F9; color: #374151; vertical-align: top; line-height: 1.55; }
  .pol-table tr:last-child td { border-bottom: none; }
  .pol-table tr:nth-child(even) td { background: #FAFBFC; }
  .pol-updated-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); border-radius: 20px; padding: 5px 12px; font-size: 12px; color: #CBD5E1; margin-top: 14px; }
  .pol-breadcrumb { display: flex; align-items: center; gap: 6px; justify-content: center; margin-bottom: 16px; }
  .pol-breadcrumb-item { font-size: 13px; color: #94A3B8; cursor: pointer; transition: color 0.15s; background: none; border: none; font-family: inherit; padding: 0; }
  .pol-breadcrumb-item:hover { color: #CBD5E1; }
  .pol-breadcrumb-sep { color: #475569; font-size: 12px; }
  @media (max-width: 900px) {
    .pol-nav-center { display: none !important; }
    .pol-footer-grid { flex-direction: column !important; gap: 32px !important; }
  }
  @media (max-width: 680px) {
    .pol-card-header { padding: 24px 22px 20px !important; }
    .pol-card-body { padding: 0 22px 32px !important; }
    .pol-section-body { padding-left: 0 !important; }
    .pol-section-num { width: 28px; height: 28px; font-size: 11px; }
    .pol-summary-grid { grid-template-columns: 1fr !important; }
    .pol-toc-grid { grid-template-columns: 1fr 1fr !important; }
  }
  @media (max-width: 420px) {
    .pol-card-header { padding: 18px 16px 16px !important; }
    .pol-card-body { padding: 0 16px 24px !important; }
    .pol-toc-grid { grid-template-columns: 1fr !important; }
  }
`;

const accentGradients = {
  '#EF1D28': { g: 'linear-gradient(135deg,#EF1D28,#FF5C5C)', s: '0 3px 10px rgba(239,29,40,0.25)', bg1: '#FFF1F2', bg2: '#FFF5F5', border: '#FECACA' },
  '#2563EB': { g: 'linear-gradient(135deg,#2563EB,#60A5FA)', s: '0 3px 10px rgba(37,99,235,0.25)', bg1: '#EFF6FF', bg2: '#F0F9FF', border: '#BFDBFE' },
  '#059669': { g: 'linear-gradient(135deg,#059669,#34D399)', s: '0 3px 10px rgba(5,150,105,0.25)', bg1: '#F0FDF4', bg2: '#ECFDF5', border: '#BBF7D0' },
  '#D97706': { g: 'linear-gradient(135deg,#D97706,#FBBF24)', s: '0 3px 10px rgba(217,119,6,0.25)', bg1: '#FFFBEB', bg2: '#FFF7ED', border: '#FDE68A' },
};

const PolicyLayout = ({ title, lastUpdated, icon, accentColor = '#EF1D28', children }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDropdown, setOpenDropdown] = useState(null);
  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const ag = accentGradients[accentColor] || accentGradients['#EF1D28'];

  const cssVars = {
    '--pol-accent': accentColor,
    '--pol-accent-gradient': ag.g,
    '--pol-accent-shadow': ag.s,
    '--pol-summary-bg1': ag.bg1,
    '--pol-summary-bg2': ag.bg2,
    '--pol-summary-border': ag.border,
  };

  return (
    <div style={{ minHeight:'100vh', background:'#F8FAFC', fontFamily:'Inter, sans-serif', ...cssVars }}>
      <style>{POLICY_NAV_CSS}</style>
      <MarketingHeader />

      {/* ── Navbar ── */}
      <nav style={{ display:'none', background:'rgba(255,255,255,0.97)', backdropFilter:'blur(12px)', borderBottom:'1px solid #F3F4F6', padding:'0 32px', alignItems:'center', justifyContent:'space-between', height:64, position:'sticky', top:0, zIndex:100 }}>
        <button onClick={() => navigate('/')} style={{ display:'flex', alignItems:'center', gap:10, border:'none', background:'transparent', cursor:'pointer', padding:0, flexShrink:0 }}>
          <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:34, height:34, objectFit:'contain' }} />
          <div style={{ textAlign:'left' }}>
            <div style={{ fontSize:16, fontWeight:900, color:'#EF1D28', lineHeight:1.1 }}>MarsResume</div>
            <div style={{ fontSize:9, color:'#9CA3AF', fontWeight:400, lineHeight:1 }}>Resume That Takes You to Mars</div>
          </div>
        </button>

        <div className="pol-nav-center" style={{ display:'flex', alignItems:'center', gap:0 }}>
          {/* Features dropdown */}
          <div style={{ position:'relative' }} onMouseEnter={() => setOpenDropdown('features')} onMouseLeave={() => setOpenDropdown(null)}>
            <button className="pol-nav-link" style={{ display:'flex', alignItems:'center', gap:4, color: openDropdown==='features' ? '#EF1D28' : undefined }}>
              Features <ChevronDown size={13} />
            </button>
            {openDropdown === 'features' && (
              <div style={{ position:'absolute', top:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)', background:'white', border:'1px solid #E5E7EB', borderRadius:16, boxShadow:'0 20px 60px rgba(0,0,0,0.13)', padding:'20px', display:'grid', gridTemplateColumns:'220px 220px 190px', gap:0, zIndex:300, minWidth:680 }}>
                <div style={{ paddingRight:16, borderRight:'1px solid #F3F4F6' }}>
                  <div style={{ fontSize:10, fontWeight:800, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:1, marginBottom:8, paddingLeft:4 }}>Build</div>
                  {[
                    { Icon:FileText, bg:'#FFF1F2', ic:'#EF1D28', title:'Resume Builder', desc:'Create your best resume.' },
                    { Icon:Mail,     bg:'#F0F9FF', ic:'#0891B2', title:'Cover Letter Builder', desc:'Attach a matching cover letter.' },
                    { Icon:Sparkles, bg:'#F5F3FF', ic:'#7C3AED', title:'AI Resume Writer', desc:'Let AI write your resume.' },
                    { Icon:Zap,      bg:'#FFFBEB', ic:'#D97706', title:'AI Cover Letter Writer', desc:'AI-powered cover letter writer.' },
                  ].map(({ Icon, bg, ic, title: t, desc }) => (
                    <button key={t} onClick={() => { goTo('/signup'); setOpenDropdown(null); }}
                      style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 8px', borderRadius:10, border:'none', background:'none', cursor:'pointer', width:'100%', textAlign:'left', marginBottom:2 }}
                      onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <div style={{ width:34, height:34, borderRadius:8, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon size={15} color={ic} /></div>
                      <div><div style={{ fontWeight:700, fontSize:13, color:'#111827', marginBottom:1 }}>{t}</div><div style={{ fontSize:11, color:'#6B7280', lineHeight:1.4 }}>{desc}</div></div>
                    </button>
                  ))}
                </div>
                <div style={{ paddingLeft:16, paddingRight:16, borderRight:'1px solid #F3F4F6' }}>
                  <div style={{ fontSize:10, fontWeight:800, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:1, marginBottom:8, paddingLeft:4 }}>AI Tools</div>
                  {[
                    { Icon:Target,   bg:'#ECFDF5', ic:'#059669', title:'ATS Checker', desc:'Get instant feedback.' },
                    { Icon:BarChart2, bg:'#EFF6FF', ic:'#2563EB', title:'AI Job Tailoring', desc:'Tailor to any job.' },
                    { Icon:Users,    bg:'#FDF2F8', ic:'#DB2777', title:'Interview Prep', desc:'Ace your next interview.' },
                    { Icon:Download, bg:'#F0FDF4', ic:'#16A34A', title:'PDF Export', desc:'Export in one click.' },
                  ].map(({ Icon, bg, ic, title: t, desc }) => (
                    <button key={t} onClick={() => { goTo('/signup'); setOpenDropdown(null); }}
                      style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 8px', borderRadius:10, border:'none', background:'none', cursor:'pointer', width:'100%', textAlign:'left', marginBottom:2 }}
                      onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <div style={{ width:34, height:34, borderRadius:8, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon size={15} color={ic} /></div>
                      <div><div style={{ fontWeight:700, fontSize:13, color:'#111827', marginBottom:1 }}>{t}</div><div style={{ fontSize:11, color:'#6B7280', lineHeight:1.4 }}>{desc}</div></div>
                    </button>
                  ))}
                </div>
                <div style={{ paddingLeft:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#111827', marginBottom:12 }}>Resume Examples</div>
                  {['Software Engineering','Engineering','Back-End Developer','Student Internship','Business','Healthcare'].map(ex => (
                    <button key={ex} onClick={() => { goTo('/templates#resumes'); setOpenDropdown(null); }}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'8px 0', border:'none', background:'none', cursor:'pointer', fontSize:13, color:'#374151', borderBottom:'1px solid #F3F4F6', textAlign:'left' }}
                      onMouseEnter={e => e.currentTarget.style.color='#EF1D28'} onMouseLeave={e => e.currentTarget.style.color='#374151'}>
                      {ex} <ArrowRight size={12} color="#9CA3AF" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resume dropdown */}
          <div style={{ position:'static' }} onMouseEnter={() => setOpenDropdown('resume')} onMouseLeave={() => setOpenDropdown(null)}>
            <button className="pol-nav-link" style={{ display:'flex', alignItems:'center', gap:4, color: openDropdown==='resume' ? '#EF1D28' : undefined }}>
              Resume <ChevronDown size={13} />
            </button>
            {openDropdown === 'resume' && <HeaderMegaMenu title="Explore Resumes" sections={RESUME_MEGA_SECTIONS} handleNav={(path) => {
              goTo(path);
              setOpenDropdown(null);
            }} />}
          </div>

          {/* Cover Letter dropdown */}
          <div style={{ position:'static' }} onMouseEnter={() => setOpenDropdown('coverletter')} onMouseLeave={() => setOpenDropdown(null)}>
            <button className="pol-nav-link" style={{ display:'flex', alignItems:'center', gap:4, color: openDropdown==='coverletter' ? '#EF1D28' : undefined }}>
              Cover Letter <ChevronDown size={13} />
            </button>
            {openDropdown === 'coverletter' && <HeaderMegaMenu title="Explore Cover Letters" sections={COVER_LETTER_MEGA_SECTIONS} handleNav={(path) => {
              goTo(path);
              setOpenDropdown(null);
            }} />}
          </div>

          <button className="pol-nav-link" onClick={() => goTo('/signup')}>ATS Checker</button>
          <button className="pol-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
          {user ? (
            <>
              <span style={{ color:'#9CA3AF', fontSize:13, padding:'0 8px', fontWeight:500 }}>Hi, {user.name?.split(' ')[0]} 👋</span>
              <button onClick={() => navigate('/dashboard')} className="pol-btn-primary">Dashboard <ArrowRight size={14} /></button>
            </>
          ) : (
            <>
              <button onClick={() => navigate('/login')}
                style={{ padding:'8px 20px', fontSize:13, fontWeight:700, borderRadius:9, border:'1.5px solid #EF1D28', background:'white', color:'#EF1D28', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background='#FFF1F2'} onMouseLeave={e => e.currentTarget.style.background='white'}>
                Log In
              </button>
              <button onClick={() => navigate('/signup')} className="pol-btn-primary">Create My Resume</button>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ background:'linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#0F172A 100%)', padding:'52px 32px 60px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-60, left:'50%', transform:'translateX(-50%)', width:600, height:300, background:`radial-gradient(ellipse, ${accentColor}25 0%, transparent 70%)`, pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:1, background:`linear-gradient(90deg, transparent, ${accentColor}40, transparent)` }} />
        <div style={{ maxWidth:760, margin:'0 auto', textAlign:'center', position:'relative' }}>
          <div className="pol-breadcrumb">
            <button className="pol-breadcrumb-item" onClick={() => navigate('/')}>Home</button>
            <span className="pol-breadcrumb-sep">›</span>
            <span style={{ fontSize:13, color:'#94A3B8' }}>Legal</span>
            <span className="pol-breadcrumb-sep">›</span>
            <span style={{ fontSize:13, color:'#CBD5E1', fontWeight:600 }}>{title}</span>
          </div>
          <div style={{ width:52, height:52, borderRadius:16, background:`linear-gradient(135deg,${accentColor}30,${accentColor}15)`, border:`1px solid ${accentColor}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, margin:'0 auto 16px', boxShadow:`0 8px 24px ${accentColor}20` }}>{icon}</div>
          <h1 style={{ margin:'0 0 8px', fontSize:40, fontWeight:900, color:'white', letterSpacing:-1, lineHeight:1.1 }}>{title}</h1>
          <div className="pol-updated-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Last updated: {lastUpdated}
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <div style={{ maxWidth:860, margin:'0 auto', padding:'40px 24px 80px' }}>
        <div className="pol-content-card" style={{ background:'white', borderRadius:20, border:'1px solid #E2E8F0', boxShadow:'0 8px 40px rgba(0,0,0,0.08)' }}>
          <div className="pol-card-header">
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:38, height:38, borderRadius:11, background:'linear-gradient(135deg,#EF1D28,#FF5C5C)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, boxShadow:'0 4px 12px rgba(239,29,40,0.28)', flexShrink:0 }}>{icon}</div>
              <div>
                <h1 style={{ margin:0, fontSize:20, fontWeight:900, color:'#0F172A', letterSpacing:-0.3 }}>{title}</h1>
                <p style={{ margin:'3px 0 0', fontSize:12.5, color:'#94A3B8' }}>Last updated: {lastUpdated} · Devops Markaz Tech Pvt. Ltd.</p>
              </div>
            </div>
          </div>
          <div className="pol-card-body">
            {children}
          </div>
        </div>

        {/* Bottom nav */}
        <div style={{ marginTop:32, display:'flex', justifyContent:'center', gap:20, flexWrap:'wrap' }}>
          {[['Privacy Policy','/privacy-policy'],['Terms of Use','/terms-of-use'],['Cookie Policy','/cookie-policy'],['Contact Us','/contact']].map(([label, path]) => (
            <button key={label} onClick={() => navigate(path)}
              style={{ background:'none', border:'none', color:'#9CA3AF', fontSize:13, cursor:'pointer', fontFamily:'inherit', padding:0, transition:'color 0.15s', textDecoration:'underline', textDecorationColor:'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.color='#EF1D28'; e.currentTarget.style.textDecorationColor='#EF1D28'; }}
              onMouseLeave={e => { e.currentTarget.style.color='#9CA3AF'; e.currentTarget.style.textDecorationColor='transparent'; }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Footer ── */}
      <MarketingFooter navigate={navigate} goTo={goTo} />
      <footer style={{ display:'none', background:'#FAFAFA', borderTop:'1px solid #F3F4F6', padding:'72px 48px 0' }}>
        <div style={{ maxWidth:1140, margin:'0 auto' }}>
          <div className="pol-footer-grid" style={{ paddingBottom:56, borderBottom:'1px solid #E5E7EB' }}>
            <div style={{ flex:'0 0 auto', maxWidth:280 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width:34, height:34, objectFit:'contain' }} />
                <div>
                  <div style={{ color:'#EF1D28', fontWeight:900, fontSize:17, lineHeight:1 }}>MarsResume</div>
                  <div style={{ color:'#9CA3AF', fontSize:9, marginTop:3, fontWeight:400 }}>Resume That Takes You to Mars</div>
                </div>
              </div>
              <p style={{ margin:'0 0 20px', fontSize:13, color:'#6B7280', lineHeight:1.8 }}>AI-powered resume builder helping professionals worldwide land better jobs, faster.</p>
              <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                {SOCIAL_LINKS.map(({ Icon, label, href }) => (
                  <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer" style={{ width:36, height:36, borderRadius:10, background:'white', border:'1.5px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', transition:'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background='#F9FAFB'; e.currentTarget.style.borderColor='#D1D5DB'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='white'; e.currentTarget.style.borderColor='#E5E7EB'; }}>
                    <Icon size={14} color="#6B7280" />
                  </a>
                ))}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                {[{icon:ShieldCheck,text:'SSL encrypted & secure'},{icon:Users,text:'100+ professionals'}].map(({ icon: Icon, text }, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12, color:'#9CA3AF' }}><Icon size={13} color="#D1D5DB" /> {text}</div>
                ))}
              </div>
            </div>

            <div style={{ display:'flex', gap:56, flexWrap:'wrap' }}>
              {[
                { heading:'Product', links:[['Resume Builder',() => goTo('/signup')],['CV Builder',() => goTo('/signup')],['Cover Letter',() => goTo('/signup')],['ATS Analyzer',() => navigate('/ats-checker')],['Pricing',() => navigate('/pricing')]] },
                { heading:'Resources', links:[['How It Works',()=> navigate('/how-it-works')],['ATS Guide',()=> navigate('/ats-checker')],['Resume Tips',()=> navigate('/resume-tips')],['Interview Prep',() => goTo('/signup')],['Career Blog',() => navigate('/blog')]] },
                { heading:'Company', links:[['About Us',() => navigate('/about')],['Contact',() => navigate('/contact')],['Privacy Policy',() => navigate('/privacy-policy')],['Terms of Service',() => navigate('/terms-of-use')],['Cookie Policy',() => navigate('/cookie-policy')]] },
              ].map(({ heading, links }) => (
                <div key={heading}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#111827', textTransform:'uppercase', letterSpacing:0.8, marginBottom:20 }}>{heading}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                    {links.map(([label, onClick]) => (
                      <button key={label} onClick={onClick}
                        style={{ background:'none', border:'none', color:'#6B7280', fontSize:13, cursor:'pointer', textAlign:'left', padding:0, fontFamily:'inherit', transition:'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color='#111827'} onMouseLeave={e => e.currentTarget.style.color='#6B7280'}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding:'24px 0 32px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
            <div style={{ color:'#9CA3AF', fontSize:12 }}>© {new Date().getFullYear()} Devops Markaz Tech Pvt. Ltd. · All rights reserved</div>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              {[['Privacy','/privacy-policy'],['Terms','/terms-of-use'],['Contact','/contact']].map(([label, path]) => (
                <button key={label} onClick={() => navigate(path)}
                  style={{ background:'none', border:'none', color:'#9CA3AF', fontSize:12, cursor:'pointer', fontFamily:'inherit', padding:0, transition:'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#374151'} onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PolicySection = ({ title, children }) => {
  const match = title.match(/^(\d+)\.\s+(.+)$/);
  const num = match ? match[1] : null;
  const titleText = match ? match[2] : title;
  const id = `section-${(titleText || title).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div id={id} className="pol-section">
      <div className="pol-section-header">
        {num && <div className="pol-section-num">{num}</div>}
        <h2 className="pol-section-title">{titleText}</h2>
      </div>
      <div className={num ? 'pol-section-body' : ''}>
        {children}
      </div>
    </div>
  );
};

const PolicyTOC = ({ sections }) => (
  <div className="pol-toc">
    <div className="pol-toc-title">Jump to section</div>
    <div className="pol-toc-grid">
      {sections.map(({ num, title: t }) => {
        const id = `section-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        return (
          <button key={t} className="pol-toc-link" onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span className="pol-toc-num">{num}</span>
            <span>{t}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ── Terms of Use ──────────────────────────────────────────────────────────────
export const TermsOfUsePage = () => {
  usePageSEO({ title: 'Terms of Use – MarsResume', description: 'Read the Terms of Use for MarsResume, the free AI-powered resume builder by Devops Markaz Tech Pvt. Ltd.', canonical: '/terms-of-use' });
  return (<PolicyLayout title="Terms of Use" lastUpdated="June 10, 2026" icon="📋" accentColor="#2563EB">

    <div className="pol-summary" style={{ marginTop: 28 }}>
      <div className="pol-summary-title">TL;DR — Plain English Summary</div>
      <div className="pol-summary-grid">
        {[
          'You must be 16+ to use MarsResume.',
          'You own all resume content you create.',
          'Free plan has limits; Pro unlocks everything.',
          'Payments processed securely by Stripe.',
          'Cancel anytime — Pro continues until period ends.',
          'Refund requests within 7 days are considered.',
          'AI content is a writing aid — review before use.',
          'Abuse, fraud, or misuse may result in termination.',
        ].map(item => (
          <div key={item} className="pol-summary-item">
            <span className="pol-summary-dot" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>

    <PolicyTOC sections={[
      { num:'1', title:'Acceptance of Terms' },
      { num:'2', title:'About MarsResume' },
      { num:'3', title:'Eligibility' },
      { num:'4', title:'Account Registration & Security' },
      { num:'5', title:'Free and Pro Subscription Plans' },
      { num:'6', title:'Billing, Payments & Auto-Renewal' },
      { num:'7', title:'Refund & Cancellation Policy' },
      { num:'8', title:'Referral Program' },
      { num:'9', title:'AI-Powered Features' },
      { num:'10', title:'User Content & Data' },
      { num:'11', title:'Prohibited Conduct' },
      { num:'12', title:'Intellectual Property' },
      { num:'13', title:'Third-Party Services' },
      { num:'14', title:'Disclaimer of Warranties' },
      { num:'15', title:'Limitation of Liability' },
      { num:'16', title:'Indemnification' },
      { num:'17', title:'Governing Law & Jurisdiction' },
      { num:'18', title:'Termination' },
      { num:'19', title:'Changes to These Terms' },
      { num:'20', title:'Contact Us' },
    ]} />

    <PolicySection title="1. Acceptance of Terms">
      <p className="pol-p">By accessing, browsing, or using MarsResume and its associated services ("the Service"), operated by <strong>Devops Markaz Tech Pvt. Ltd.</strong> ("we", "us", or "our"), you confirm that you have read, understood, and agree to be legally bound by these Terms of Use and our Privacy Policy. If you do not agree to any part of these Terms, you must discontinue use of the Service immediately.</p>
      <p className="pol-p">These Terms apply to all users of the Service, including visitors, registered users, and paying subscribers.</p>
    </PolicySection>

    <PolicySection title="2. About MarsResume">
      <p className="pol-p">MarsResume is an AI-powered career platform that helps professionals create, customize, and export world-class resumes, CVs, and cover letters. Our Service includes:</p>
      <ul className="pol-ul">
        <li><strong>Resume Builder</strong> — Create and edit professional resumes with 9+ ATS-optimized templates</li>
        <li><strong>CV Builder</strong> — Build comprehensive academic and professional CVs</li>
        <li><strong>Cover Letter Builder</strong> — Write tailored cover letters with AI assistance</li>
        <li><strong>AI Writing Assistant</strong> — AI-powered content generation and improvement powered by Anthropic Claude</li>
        <li><strong>ATS Checker</strong> — Analyze resumes against job descriptions for keyword match scores</li>
        <li><strong>Job Search</strong> — Discover relevant job listings with dual-API search</li>
        <li><strong>Interview Preparation</strong> — Practice with AI-generated interview questions</li>
        <li><strong>PDF &amp; DOCX Export</strong> — Download finished documents in professional formats</li>
        <li><strong>Referral Program</strong> — Earn points and free Pro days by referring friends</li>
      </ul>
    </PolicySection>

    <PolicySection title="3. Eligibility">
      <p className="pol-p">You must be at least <strong>16 years of age</strong> to use this Service. By using MarsResume, you represent and warrant that you meet this age requirement and that you have the legal capacity to enter into a binding agreement. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.</p>
    </PolicySection>

    <PolicySection title="4. Account Registration & Security">
      <p className="pol-p">To access certain features, you must create an account via email registration, Google OAuth, or LinkedIn OAuth. You agree to:</p>
      <ul className="pol-ul">
        <li>Provide accurate, current, and complete information during registration</li>
        <li>Maintain and promptly update your account information</li>
        <li>Keep your password secure and confidential — do not share it with anyone</li>
        <li>Accept full responsibility for all activities that occur under your account</li>
        <li>Notify us immediately at <a href="mailto:support@marsresume.com" style={{color:'#EF1D28'}}>support@marsresume.com</a> if you suspect unauthorized account access</li>
        <li>Not create multiple accounts to circumvent plan restrictions or abuse free credits</li>
      </ul>
      <p className="pol-p">We reserve the right to suspend or terminate accounts that violate these Terms.</p>
    </PolicySection>

    <PolicySection title="5. Free and Pro Subscription Plans">
      <p className="pol-p"><strong>Free Plan:</strong> Provides access to basic resume creation, limited templates, and standard export options at no charge. Free plan users may encounter feature limitations as described on our <a href="/pricing" style={{color:'#EF1D28'}}>Pricing page</a>.</p>
      <p className="pol-p"><strong>Pro Plan:</strong> Provides full access to all premium templates, AI-powered writing tools, unlimited document creation, ATS analysis, priority export, and all advanced features for a monthly subscription fee.</p>
      <p className="pol-p">We reserve the right to modify the features included in each plan, pricing, and availability with reasonable prior notice. Material changes will be communicated by email or in-app notification.</p>
    </PolicySection>

    <PolicySection title="6. Billing, Payments & Auto-Renewal">
      <p className="pol-p">All payments are processed securely through <strong>Stripe</strong>, a PCI-DSS compliant payment processor. By subscribing to a Pro plan, you agree to:</p>
      <ul className="pol-ul">
        <li>Provide valid payment information and authorize recurring charges</li>
        <li>Pay the applicable subscription fee at the beginning of each billing cycle</li>
        <li>Acknowledge that your subscription will automatically renew at the end of each billing period unless cancelled</li>
        <li>Be responsible for all applicable taxes in your jurisdiction</li>
      </ul>
      <p className="pol-p">We do not store your full card number — all payment data is handled by Stripe.</p>
    </PolicySection>

    <PolicySection title="7. Refund & Cancellation Policy">
      <p className="pol-p">You may cancel your Pro subscription at any time from your <strong>My Plan</strong> section in the dashboard. Upon cancellation:</p>
      <ul className="pol-ul">
        <li>Your Pro access continues until the end of the current billing period</li>
        <li>You will not be charged for future billing cycles</li>
        <li>No partial refunds are issued for unused portions of the current billing period</li>
      </ul>
      <div className="pol-highlight">
        <strong>Exceptions:</strong> Refund requests within 7 days of the initial subscription purchase may be considered on a case-by-case basis. Contact us at <a href="mailto:support@marsresume.com" style={{color:'#C5161D'}}>support@marsresume.com</a> with your order details.
      </div>
    </PolicySection>

    <PolicySection title="8. Referral Program">
      <p className="pol-p">MarsResume offers a referral program that rewards users for inviting others to the platform. By participating, you agree that:</p>
      <ul className="pol-ul">
        <li>Referral rewards (points and free Pro days) are granted upon valid referral actions (new user signup and first Pro subscription)</li>
        <li>Points expire 30 days after being earned and reset to zero upon redemption</li>
        <li>60 accumulated points may be redeemed for 30 days of Pro access</li>
        <li>Abuse of the referral program (fake accounts, self-referrals, fraudulent signups) will result in forfeiture of all points and possible account termination</li>
        <li>We reserve the right to modify or discontinue the referral program at any time</li>
      </ul>
    </PolicySection>

    <PolicySection title="9. AI-Powered Features">
      <p className="pol-p">MarsResume uses advanced AI technology powered by <strong>Anthropic Claude</strong> to provide writing suggestions, resume improvements, cover letter generation, ATS analysis, and interview preparation. By using these AI features, you acknowledge:</p>
      <ul className="pol-ul">
        <li>AI-generated content is provided as a writing aid and suggestion tool, not as professional career advice</li>
        <li>You are responsible for reviewing, editing, and verifying all AI-generated content before use</li>
        <li>We do not guarantee that AI outputs will be accurate, complete, or suitable for your specific situation</li>
        <li>Your resume content is sent to Anthropic's API to generate results — we do not use your personal data to train AI models</li>
        <li>AI features are subject to rate limits and availability, which may vary by subscription plan</li>
      </ul>
    </PolicySection>

    <PolicySection title="10. User Content & Data">
      <p className="pol-p">You retain full ownership of the resume content, personal information, and documents you create using MarsResume ("User Content"). By using the Service, you grant us a limited, non-exclusive license to store, display, and process your User Content solely to provide and improve the Service.</p>
      <ul className="pol-ul">
        <li>You are responsible for ensuring your User Content does not infringe third-party rights</li>
        <li>We do not claim ownership of your documents or personal information</li>
        <li>You may delete your account and all associated data at any time</li>
        <li>We will not sell your User Content to third parties</li>
      </ul>
    </PolicySection>

    <PolicySection title="11. Prohibited Conduct">
      <p className="pol-p">You agree not to:</p>
      <ul className="pol-ul">
        <li>Use the Service for any unlawful, harmful, or fraudulent purpose</li>
        <li>Create resumes containing false, fabricated, or misleading information intended to deceive employers</li>
        <li>Attempt to gain unauthorized access to any part of the Service, servers, or databases</li>
        <li>Reverse engineer, decompile, or disassemble any portion of the platform</li>
        <li>Use automated bots, scrapers, or scripts to access or extract data from the Service</li>
        <li>Distribute malware, viruses, or malicious code through the Service</li>
        <li>Harass, abuse, or harm other users</li>
        <li>Resell, sublicense, or commercially exploit the Service without written permission</li>
        <li>Circumvent security measures, rate limits, or subscription restrictions</li>
        <li>Use the AI tools to generate content that is hateful, illegal, or deceptive</li>
      </ul>
    </PolicySection>

    <PolicySection title="12. Intellectual Property">
      <p className="pol-p">MarsResume, its logos, designs, templates, source code, AI models, and all original content are the exclusive intellectual property of <strong>Devops Markaz Tech Pvt. Ltd.</strong> and are protected by copyright, trademark, and other applicable laws.</p>
      <p className="pol-p">Our resume templates are licensed for personal, non-commercial use. You may not resell, redistribute, or commercially exploit our templates. You retain ownership of the personal content you enter into the templates.</p>
    </PolicySection>

    <PolicySection title="13. Third-Party Services">
      <p className="pol-p">MarsResume integrates with third-party services to deliver the platform. These include:</p>
      <ul className="pol-ul">
        <li><strong>Stripe</strong> — Payment processing. Governed by <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Stripe's Privacy Policy</a>.</li>
        <li><strong>Google OAuth</strong> — Optional sign-in. Governed by Google's Terms and Privacy Policy.</li>
        <li><strong>LinkedIn OAuth</strong> — Optional sign-in. Governed by LinkedIn's Terms and Privacy Policy.</li>
        <li><strong>Anthropic</strong> — AI content generation. Your resume content is processed via their API under their data handling policies.</li>
      </ul>
      <p className="pol-p">We are not responsible for the practices, content, or availability of third-party services. Your use of these services is subject to their respective terms.</p>
    </PolicySection>

    <PolicySection title="14. Disclaimer of Warranties">
      <p className="pol-p">The Service is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis without warranties of any kind, express or implied. We do not warrant that:</p>
      <ul className="pol-ul">
        <li>The Service will be uninterrupted, timely, or error-free</li>
        <li>AI-generated content will be accurate, complete, or suitable for every purpose</li>
        <li>Using our Service will guarantee employment or interview success</li>
        <li>The Service will meet your specific requirements or expectations</li>
      </ul>
    </PolicySection>

    <PolicySection title="15. Limitation of Liability">
      <p className="pol-p">To the maximum extent permitted by applicable law, <strong>Devops Markaz Tech Pvt. Ltd.</strong> and its directors, employees, and partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising from your use of or inability to use the Service.</p>
      <p className="pol-p">Our total liability to you for any claim arising under these Terms shall not exceed the amount you paid us in the 12 months prior to the claim.</p>
    </PolicySection>

    <PolicySection title="16. Indemnification">
      <p className="pol-p">You agree to indemnify, defend, and hold harmless Devops Markaz Tech Pvt. Ltd. and its officers, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from your violation of these Terms, your User Content, or your misuse of the Service.</p>
    </PolicySection>

    <PolicySection title="17. Governing Law & Jurisdiction">
      <p className="pol-p">These Terms are governed by and construed in accordance with the laws of <strong>Pakistan</strong>, without regard to conflict of law principles. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Lahore, Pakistan.</p>
    </PolicySection>

    <PolicySection title="18. Termination">
      <p className="pol-p">We reserve the right to suspend or terminate your account at our discretion if you violate these Terms, engage in fraudulent activity, or cause harm to the Service or other users. You may also delete your account at any time from your Profile settings. Upon termination, your right to use the Service ceases immediately.</p>
    </PolicySection>

    <PolicySection title="19. Changes to These Terms">
      <p className="pol-p">We may update these Terms at any time. When we make material changes, we will notify you by email (to the address on your account) and/or by posting a prominent notice on the Service. Continued use of the Service after the effective date of any changes constitutes acceptance of the revised Terms.</p>
    </PolicySection>

    <PolicySection title="20. Contact Us">
      <p className="pol-p">For questions, concerns, or feedback regarding these Terms of Use, please contact us:</p>
      <ul className="pol-ul">
        <li><strong>Email:</strong> <a href="mailto:support@marsresume.com" style={{color:'#EF1D28'}}>support@marsresume.com</a></li>
        <li><strong>Company:</strong> Devops Markaz Tech Pvt. Ltd.</li>
        <li><strong>Website:</strong> <a href="https://marsresume.com" style={{color:'#EF1D28'}}>marsresume.com</a></li>
      </ul>
    </PolicySection>

  </PolicyLayout>
  );
};

// ── Cookie Policy ─────────────────────────────────────────────────────────────
export const CookiePolicyPage = () => {
  usePageSEO({ title: 'Cookie Policy – MarsResume', description: 'Understand how MarsResume uses cookies to provide a secure, personalized experience. Learn how to manage your cookie preferences.', canonical: '/cookie-policy' });
  return (<PolicyLayout title="Cookie Policy" lastUpdated="June 13, 2026" icon="🍪" accentColor="#D97706">

    <div className="pol-summary" style={{ marginTop: 28 }}>
      <div className="pol-summary-title">TL;DR — Cookies at a Glance</div>
      <div className="pol-summary-grid">
        {[
          'We use localStorage (not traditional cookies) for auth & preferences.',
          'Your JWT login token is stored in localStorage — not a cookie.',
          'No advertising or tracking cookies are set by MarsResume.',
          'Third parties (Google, LinkedIn, Stripe) set their own cookies.',
          'Essential storage cannot be disabled without breaking the app.',
          'Clear browser localStorage to remove all MarsResume data.',
          'Cookie consent banner shown on first visit.',
          'Contact us to withdraw consent or ask cookie questions.',
        ].map(item => (
          <div key={item} className="pol-summary-item">
            <span className="pol-summary-dot" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>

    <PolicyTOC sections={[
      { num:'1',  title:'What Are Cookies?' },
      { num:'2',  title:'Why We Use Cookies & Local Storage' },
      { num:'3',  title:'Types of Cookies We Use' },
      { num:'4',  title:'Essential Cookies & Local Storage' },
      { num:'5',  title:'Functional Cookies' },
      { num:'6',  title:'Analytics' },
      { num:'7',  title:'Third-Party Cookies' },
      { num:'8',  title:'Session vs. Persistent Storage' },
      { num:'9',  title:'Managing & Controlling Cookies' },
      { num:'10', title:'Cookie Consent' },
      { num:'11', title:'Updates to This Policy' },
      { num:'12', title:'Contact Us' },
    ]} />

    <PolicySection title="1. What Are Cookies?">
      <p className="pol-p">Cookies are small text files placed on your device (computer, tablet, or mobile phone) when you visit a website. They allow the website to recognize your device, remember your preferences, and provide you with a more personalized experience. Cookies are widely used to make websites work more efficiently and to provide information to website owners.</p>
      <p className="pol-p">In addition to cookies, MarsResume also uses <strong>localStorage</strong> — a browser storage mechanism that allows us to store information locally on your device (such as your authentication token and draft resume state) that persists across browser sessions.</p>
    </PolicySection>

    <PolicySection title="2. Why We Use Cookies & Local Storage">
      <p className="pol-p">MarsResume uses cookies and local storage to:</p>
      <ul className="pol-ul">
        <li>Keep you signed in securely across sessions</li>
        <li>Remember your in-progress resume drafts so you don't lose work</li>
        <li>Store your dashboard preferences (e.g., sidebar collapsed state, active view)</li>
        <li>Recognize referral codes passed via URL so referred users get their free Pro days</li>
        <li>Understand how users interact with the platform to fix bugs and improve features</li>
        <li>Enable third-party sign-in (Google and LinkedIn OAuth flows)</li>
        <li>Process secure payments via Stripe</li>
        <li>Remember your cookie consent preference</li>
      </ul>
    </PolicySection>

    <PolicySection title="3. Types of Cookies We Use">
      <div style={{overflowX:'auto'}}>
        <table className="pol-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Name / Key</th>
              <th>Purpose</th>
              <th>Storage Type</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Essential / Auth', 'rb_token', 'Your JWT authentication token — keeps you logged in securely', 'localStorage', 'Until logout'],
              ['Essential / Auth', 'admin_token', 'Admin panel JWT authentication token', 'localStorage', 'Until admin logout'],
              ['Functional', 'mars_onboarding_pending', 'Tracks whether you have completed the new-user onboarding flow', 'localStorage', 'Until onboarding done'],
              ['Functional', 'mars_referral_code', 'Stores a referral code from a signup URL so it persists if you sign up later', 'localStorage', 'Cleared after signup'],
              ['Functional', 'mars_cookies_accepted', 'Records your cookie consent preference', 'localStorage', 'Persistent'],
              ['Analytics', 'Usage events', 'Anonymized tracking of feature usage, page visits, and session duration to improve the product', 'Server-side / cookies', 'Up to 2 years'],
              ['Third-Party', 'Google OAuth', 'Enables "Sign in with Google" — Google sets session cookies to authenticate your identity', 'Browser cookies', 'Session / up to 2 years'],
              ['Third-Party', 'LinkedIn OAuth', 'Enables "Sign in with LinkedIn" — LinkedIn sets session cookies to authenticate your identity', 'Browser cookies', 'Session'],
              ['Third-Party', 'Stripe', 'Enables secure payment processing. Stripe may set cookies related to fraud prevention and session tracking', 'Browser cookies', 'Session / persistent'],
            ].map(([cat, name, purpose, type, duration]) => (
              <tr key={name}>
                <td><strong>{cat}</strong></td>
                <td style={{fontFamily:'monospace', fontSize:12}}>{name}</td>
                <td>{purpose}</td>
                <td>{type}</td>
                <td style={{color:'#6B7280', whiteSpace:'nowrap'}}>{duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PolicySection>

    <PolicySection title="4. Essential Cookies & Local Storage">
      <p className="pol-p">Essential cookies and local storage items are <strong>strictly necessary</strong> for the Service to function. They cannot be disabled without breaking core functionality. This includes:</p>
      <ul className="pol-ul">
        <li><strong>Authentication (rb_token):</strong> Your login session token stored in localStorage. Without this, you would need to log in on every page load.</li>
        <li><strong>Onboarding state:</strong> Tracks whether you have seen the new user onboarding flow to avoid showing it repeatedly.</li>
        <li><strong>Draft preservation:</strong> Keeps your in-progress resume content in memory so edits are not lost when navigating the dashboard.</li>
        <li><strong>Referral code persistence:</strong> Stores referral codes from URLs so that referred users still get their bonus even if they sign up days after clicking the link.</li>
      </ul>
    </PolicySection>

    <PolicySection title="5. Functional Cookies">
      <p className="pol-p">Functional cookies remember your preferences to personalize your experience:</p>
      <ul className="pol-ul">
        <li>Sidebar collapsed/expanded state in the dashboard</li>
        <li>Your last active dashboard view (Dashboard, My Documents, Job Search, etc.)</li>
        <li>Cookie banner dismissed state (so we don't show the banner every visit)</li>
        <li>Template preview and document editor state</li>
      </ul>
    </PolicySection>

    <PolicySection title="6. Analytics">
      <p className="pol-p">With your consent, we use <strong>Firebase Analytics</strong> (a Google product) to collect anonymized usage data and understand how the Service is used. Firebase Analytics data is only collected when you accept analytics cookies. The following data is collected:</p>
      <ul className="pol-ul">
        <li><strong>Page views and navigation:</strong> Which pages you visit and how you move through the app</li>
        <li><strong>Feature interactions:</strong> Which features are used most frequently (e.g. AI generation, export, template switching)</li>
        <li><strong>Session data:</strong> Session duration and engagement metrics</li>
        <li><strong>Approximate user location:</strong> Country, region (state/province), and city — derived from your IP address by Google's servers. Your precise location or GPS data is never requested or stored. This helps us understand the geographic distribution of our users</li>
        <li><strong>Device &amp; browser:</strong> Device type, operating system, and browser version for compatibility reporting</li>
      </ul>
      <p className="pol-p"><strong>This analytics data does not identify you personally</strong> and is processed in aggregate. Firebase Analytics is governed by <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Google's Privacy Policy</a>. You may opt out of Google Analytics at any time via the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Google Analytics opt-out browser add-on</a> or by selecting "Reject all" or disabling Analytics in the Cookie Preferences panel.</p>
    </PolicySection>

    <PolicySection title="7. Third-Party Cookies">
      <p className="pol-p">Certain third-party services integrated into MarsResume may set their own cookies on your device. These cookies are governed by the respective third party's privacy policies:</p>
      <ul className="pol-ul">
        <li>
          <strong>Firebase Analytics / Google Analytics (analytics cookies — with consent only):</strong> When you accept analytics cookies, Firebase Analytics sets cookies (including <code>_ga</code>, <code>_ga_*</code>) to measure user sessions, navigation, feature usage, and approximate geographic location (country, region, city via IP geolocation). Analytics data is anonymized and processed by Google. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Google's Privacy Policy</a>. You may opt out via the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Google Analytics opt-out add-on</a>.
        </li>
        <li>
          <strong>Google (OAuth Sign-In):</strong> When you click "Sign in with Google", Google sets authentication cookies to verify your identity. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Google's Privacy Policy</a>.
        </li>
        <li>
          <strong>LinkedIn (OAuth Sign-In):</strong> When you click "Sign in with LinkedIn", LinkedIn sets authentication cookies. See <a href="https://www.linkedin.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>LinkedIn's Privacy Policy</a>.
        </li>
        <li>
          <strong>Stripe (Payments):</strong> When you visit pricing or checkout pages, Stripe may set cookies for fraud prevention, session management, and payment security. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Stripe's Privacy Policy</a>.
        </li>
      </ul>
      <div className="pol-highlight">
        We have no control over third-party cookies. If you have concerns about how these providers use cookies, please refer to their respective privacy policies.
      </div>
    </PolicySection>

    <PolicySection title="8. Session vs. Persistent Storage">
      <p className="pol-p"><strong>Session storage:</strong> Temporary data cleared automatically when you close your browser tab. We use this for temporary UI state during an active editing session.</p>
      <p className="pol-p"><strong>Persistent localStorage:</strong> Data that remains on your device until explicitly cleared or until you log out. This includes your authentication token and preferences so you don't need to re-enter them every visit.</p>
      <p className="pol-p"><strong>Browser cookies:</strong> Set by third-party services (Google, LinkedIn, Stripe) with their own expiry periods as described in their privacy policies.</p>
    </PolicySection>

    <PolicySection title="9. Managing & Controlling Cookies">
      <p className="pol-p">You have multiple ways to control cookies and local storage:</p>
      <ul className="pol-ul">
        <li><strong>Browser settings:</strong> Most browsers let you view, block, or delete cookies via Settings → Privacy &amp; Security. See your browser's help documentation:
          <ul style={{marginTop:6}}>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Chrome</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Firefox</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Safari</a></li>
            <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>Microsoft Edge</a></li>
          </ul>
        </li>
        <li><strong>Clear localStorage:</strong> You can clear localStorage data from your browser's Developer Tools (Application → Local Storage → marsresume.com → Clear All)</li>
        <li><strong>Log out:</strong> Logging out of MarsResume removes your auth token from localStorage</li>
        <li><strong>Google Analytics opt-out:</strong> If we use Google Analytics, you may opt out at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{color:'#EF1D28'}}>tools.google.com/dlpage/gaoptout</a></li>
      </ul>
      <div className="pol-highlight">
        <strong>Important:</strong> Disabling essential cookies or clearing localStorage will log you out and may prevent key features from working, including saving your documents, maintaining your login session, and completing payments.
      </div>
    </PolicySection>

    <PolicySection title="10. Cookie Consent">
      <p className="pol-p">When you first visit MarsResume, we display a cookie consent banner. By clicking "Accept" or continuing to use the Service, you consent to our use of cookies and local storage as described in this policy.</p>
      <p className="pol-p">You may withdraw consent at any time by:</p>
      <ul className="pol-ul">
        <li>Clearing cookies and localStorage in your browser settings</li>
        <li>Contacting us at <a href="mailto:support@marsresume.com" style={{color:'#EF1D28'}}>support@marsresume.com</a></li>
      </ul>
      <p className="pol-p">Withdrawing consent for essential cookies will prevent the Service from functioning correctly.</p>
    </PolicySection>

    <PolicySection title="11. Updates to This Policy">
      <p className="pol-p">We may update this Cookie Policy as our use of cookies evolves or as legal requirements change. Material changes will be communicated via email or a notice on the Service. The "Last updated" date at the top of this page reflects the most recent revision.</p>
    </PolicySection>

    <PolicySection title="12. Contact Us">
      <p className="pol-p">For questions about our use of cookies, local storage, or this policy, please contact us:</p>
      <ul className="pol-ul">
        <li><strong>Email:</strong> <a href="mailto:support@marsresume.com" style={{color:'#EF1D28'}}>support@marsresume.com</a></li>
        <li><strong>Company:</strong> Devops Markaz Tech Pvt. Ltd.</li>
        <li><strong>Website:</strong> <a href="https://marsresume.com" style={{color:'#EF1D28'}}>marsresume.com</a></li>
      </ul>
    </PolicySection>

  </PolicyLayout>
  );
};

